import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  AddPhotoAlternate,
  Article,
  AssignmentTurnedIn,
  AutoStories,
  Delete,
  ExpandMore,
  FactCheck,
  FormatSize,
  Image,
  MenuBook,
  NavigateBefore,
  NavigateNext,
  OpenInNew,
  Palette,
  Print,
  Refresh,
  Save,
  Send,
  Tune,
  Wallpaper,
} from '@mui/icons-material';
import {
  Artifact,
  Book,
  BookDesignTemplate,
  BookExport,
  BookVersion,
  platformApi,
  Project,
  PublishingRequest,
  PublishingRequestUpdatePayload,
  StoryChapter,
  User,
} from '../../services/platformApi';
import { getStatusColor, languageOptions } from '../projectConstants';
import {
  BookDesignFormState,
  BookCoverItemFormState,
  BookImagePlacementFormState,
  applyBookTypeDefaults,
  applyDesignSettingsToForm,
  bookFontLabels,
  bookFontRoles,
} from './bookDesignUtils';
import { FlowEditor } from './FlowEditor';
import { BookType, LayoutUnit } from './manuscriptSchema';
import { useLanguage } from '../../LanguageContext';
import { formatTranslatedLabel } from '../../i18n';

interface DesignPublishingTabProps {
  project: Project | null;
  users: User[];
  storyDocuments: Artifact[];
  storyChapters: StoryChapter[];
  photoArtifacts: Artifact[];
  books: Book[];
  bookDesignDrafts: Book[];
  currentBook?: Book;
  activeDesignUserId?: number;
  selectedBookId: string;
  bookForm: BookDesignFormState;
  bookVersions: BookVersion[];
  bookExports: BookExport[];
  publishingRequests: PublishingRequest[];
  saving: boolean;
  stage: 'design' | 'publishing';
  onSelectBook: (bookId: string) => void;
  onCreateBookDesignDraft: (input: BookDesignDraftCreateInput) => Promise<boolean>;
  onDeleteBookDesignDraft: (bookId: number) => Promise<boolean>;
  onSaveBook: () => void;
  onSaveBookVersion: () => void;
  onApproveBook: () => void;
  onCreateBookExport: () => void;
  onRefreshBookPreview: () => void;
  onApplyDesignParameters: () => void;
  onMarkExportPrintReady: (bookExport: BookExport) => void;
  onCreatePublishingRequest: (bookExport: BookExport) => void;
  onUpdatePublishingRequest: (publishingRequest: PublishingRequest, payload: PublishingRequestUpdatePayload) => void;
  setBookForm: React.Dispatch<React.SetStateAction<BookDesignFormState>>;
}

type BookDesignDraftCreateInput = {
  name: string;
  sourceArtifactId?: number;
  bookType: BookType;
  template?: BookDesignTemplate;
};

const formatDate = (value: string | null | undefined, locale: string, notRecorded: string) =>
  value ? new Date(value).toLocaleString(locale) : notRecorded;
type CropEdge = 'top' | 'right' | 'bottom' | 'left';
const cropEdges: CropEdge[] = ['top', 'right', 'bottom', 'left'];
const isCropEdge = (value: string): value is CropEdge => cropEdges.includes(value as CropEdge);
const printProviderOptions = [
  { value: 'amazon_kdp', label: 'Amazon KDP' },
  { value: 'other_provider', label: 'Other Provider' },
];
const publishingStatusOptions = [
  'package_ready',
  'ready',
  'submitted',
  'changes_requested',
  'approved',
  'published',
  'rejected',
  'cancelled',
];

type DesignArtifactRow = {
  id: string;
  type: string;
  title: string;
  status: string;
  source: string;
};

type SettingsTableRow = {
  label: string;
  value: React.ReactNode;
  unit?: string;
};

type PreflightCheck = {
  code?: string;
  status?: string;
  message?: string;
  [key: string]: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asPreflightChecks = (value: unknown): PreflightCheck[] =>
  Array.isArray(value) ? value.map((item) => asRecord(item) as PreflightCheck) : [];

const asString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const asNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);

const isBookType = (value: string): value is BookType => value === 'text' || value === 'picture' || value === 'mixed';

const bookFontFamilyOptions = [
  { value: 'Noto Serif Hebrew', label: 'Noto Serif Hebrew', category: 'Hebrew serif' },
  { value: 'Noto Sans Hebrew', label: 'Noto Sans Hebrew', category: 'Hebrew sans' },
  { value: 'Noto Serif', label: 'Noto Serif', category: 'Book serif' },
  { value: 'Frank Ruhl Libre', label: 'Frank Ruhl Libre', category: 'Hebrew classic' },
  { value: 'Assistant', label: 'Assistant', category: 'Hebrew modern' },
  { value: 'Rubik', label: 'Rubik', category: 'Hebrew modern' },
  { value: 'David', label: 'David', category: 'Hebrew system' },
  { value: 'Arial', label: 'Arial', category: 'System sans' },
  { value: 'Georgia', label: 'Georgia', category: 'System serif' },
  { value: 'Times New Roman', label: 'Times New Roman', category: 'System serif' },
];

const bookExportTime = (bookExport: BookExport): number =>
  bookExport.created_at ? new Date(bookExport.created_at).getTime() || 0 : 0;

const sortBookExportsNewestFirst = (exports: BookExport[]): BookExport[] =>
  [...exports].sort((first, second) => second.id - first.id || bookExportTime(second) - bookExportTime(first));

const getKdpPackage = (request: PublishingRequest): Record<string, unknown> =>
  asRecord(asRecord(request.metadata_json).kdp_package);

const getKdpPackageDownloads = (request: PublishingRequest): Record<string, unknown> =>
  asRecord(getKdpPackage(request).downloads);

const getPreflightStatusColor = (
  status?: string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  if (status === 'passed' || status === 'not_applicable') {
    return status === 'passed' ? 'success' : 'default';
  }
  if (status === 'failed') {
    return 'error';
  }
  if (status === 'warning') {
    return 'warning';
  }
  return getStatusColor(status || '');
};

export const DesignPublishingTab: React.FC<DesignPublishingTabProps> = ({
  project,
  users,
  storyDocuments,
  storyChapters,
  photoArtifacts,
  books,
  bookDesignDrafts,
  currentBook,
  activeDesignUserId,
  selectedBookId,
  bookForm,
  bookVersions,
  bookExports,
  publishingRequests,
  saving,
  stage,
  onSelectBook,
  onCreateBookDesignDraft,
  onDeleteBookDesignDraft,
  onSaveBook,
  onSaveBookVersion,
  onApproveBook,
  onCreateBookExport,
  onRefreshBookPreview,
  onApplyDesignParameters,
  onMarkExportPrintReady,
  onCreatePublishingRequest,
  onUpdatePublishingRequest,
  setBookForm,
}) => {
  const { locale, t } = useLanguage();
  const [publishingDrafts, setPublishingDrafts] = useState<Record<number, PublishingRequestUpdatePayload>>({});
  const [designDetailsOpen, setDesignDetailsOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [tocDialogOpen, setTocDialogOpen] = useState(false);
  const [backgroundDialogOpen, setBackgroundDialogOpen] = useState(false);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [fontsDialogOpen, setFontsDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [indentDialogOpen, setIndentDialogOpen] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftSourceArtifactId, setDraftSourceArtifactId] = useState('');
  const [draftBookType, setDraftBookType] = useState<BookType>('text');
  const [draftTemplateId, setDraftTemplateId] = useState('');
  const [draftDialogError, setDraftDialogError] = useState<string | null>(null);
  const [designTemplates, setDesignTemplates] = useState<BookDesignTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateAppliedNotice, setTemplateAppliedNotice] = useState(false);
  const [previewPageIndex, setPreviewPageIndex] = useState(0);
  const isDesignStage = stage === 'design';
  const isPublishingStage = stage === 'publishing';
  // Guard against a stale bookExports fetch from a previously selected book
  // landing after the switch: never build the preview URL from an export that
  // doesn't actually belong to the currently selected book.
  const currentBookExports = useMemo(
    () => (currentBook ? bookExports.filter((bookExport) => bookExport.book_id === currentBook.id) : []),
    [bookExports, currentBook]
  );
  const currentBookVersions = useMemo(
    () => (currentBook ? bookVersions.filter((version) => version.book_id === currentBook.id) : []),
    [bookVersions, currentBook]
  );
  const currentPublishingRequests = useMemo(
    () => (currentBook ? publishingRequests.filter((request) => request.book_id === currentBook.id) : []),
    [publishingRequests, currentBook]
  );
  const newestBookExports = useMemo(() => sortBookExportsNewestFirst(currentBookExports), [currentBookExports]);
  const finalExports = newestBookExports.filter((bookExport) => !bookExport.metadata_json?.is_renderer_preview);
  const latestExport = finalExports[0];
  const latestPreflightReport = asRecord(latestExport?.preflight_report_json);
  const latestPreflightChecks = asPreflightChecks(latestPreflightReport.checks);
  const failedPreflightCount = asNumber(latestPreflightReport.failed_count) ?? latestPreflightChecks.filter((check) => check.status === 'failed').length;
  const warningPreflightCount =
    asNumber(latestPreflightReport.warning_count) ?? latestPreflightChecks.filter((check) => check.status === 'warning').length;
  const storedExports = newestBookExports.filter((bookExport) => Boolean(bookExport.storage_key));
  const previewExport =
    storedExports.find((bookExport) => Boolean(bookExport.metadata_json?.is_renderer_preview)) || storedExports[0];
  const printReadyExport = newestBookExports
    .find((bookExport) => bookExport.status === 'approved' && bookExport.preflight_status === 'passed');
  const latestPublishingRequest = [...currentPublishingRequests].reverse()[0];
  const rendererPreviewUrl =
    project && currentBook && previewExport
      ? platformApi.getProjectBookExportPreviewUrl(project.id, currentBook.id, previewExport.id)
      : '';
  const previewCacheKey = previewExport ? `${previewExport.id}-${previewExport.created_at || ''}` : '';
  const rendererPreviewHtmlUrl =
    project && currentBook ? platformApi.getProjectBookPreviewHtmlUrl(project.id, currentBook.id) : '';
  const vivliostyleBookViewerUrl = rendererPreviewHtmlUrl ? platformApi.getVivliostyleViewerUrl(rendererPreviewHtmlUrl) : '';
  const previewMetadata = previewExport?.metadata_json || {};
  const previewContentType =
    typeof previewMetadata.content_type === 'string' ? previewMetadata.content_type.split(';', 1)[0] : '';
  const isPdfPreview = previewContentType === 'application/pdf';
  const previewPages = Array.isArray(previewMetadata.preview_pages)
    ? (previewMetadata.preview_pages as Array<Record<string, unknown>>)
    : [];
  const previewPageCount =
    typeof previewMetadata.preview_page_count === 'number' ? previewMetadata.preview_page_count : previewPages.length;
  const selectedPreviewPageIndex = Math.min(previewPageIndex, Math.max(previewPages.length - 1, 0));
  const selectedPreviewPage = previewPages[selectedPreviewPageIndex];
  const selectedPreviewPageAnchor =
    typeof selectedPreviewPage?.anchor === 'string'
      ? selectedPreviewPage.anchor
      : previewPages.length > 0
        ? `talegence-preview-page-${selectedPreviewPageIndex + 1}`
        : '';
  const pagedRendererPreviewUrl =
    rendererPreviewUrl && selectedPreviewPageAnchor && !isPdfPreview
      ? `${rendererPreviewUrl}#${selectedPreviewPageAnchor}`
      : rendererPreviewUrl;
  const latestPublishingChecklist = latestPublishingRequest
    ? asRecord(asRecord(latestPublishingRequest.metadata_json).submission_checklist)
    : {};
  const latestPublishingDownloads = latestPublishingRequest ? getKdpPackageDownloads(latestPublishingRequest) : {};
  const selectedLanguage = languageOptions.find((option) => option.value === bookForm.language);
  const previewPreviousIcon = bookForm.textDirection === 'rtl' ? <NavigateNext /> : <NavigateBefore />;
  const previewNextIcon = bookForm.textDirection === 'rtl' ? <NavigateBefore /> : <NavigateNext />;
  const selectedDraftTemplate = designTemplates.find((template) => template.id.toString() === draftTemplateId);
  const activeDraftCreator = activeDesignUserId ? users.find((user) => user.id === activeDesignUserId) : undefined;
  const preferredDraftSourceArtifact = useMemo(
    () =>
      storyDocuments.find((artifact) => Boolean(asRecord(artifact.metadata_json).extracted_manuscript_json)) ||
      storyDocuments[0],
    [storyDocuments]
  );

  useEffect(() => {
    setPreviewPageIndex(0);
  }, [previewExport?.id, previewPages.length]);

  useEffect(() => {
    if (isDesignStage && !currentBook) {
      setDraftDialogOpen(true);
    }
  }, [currentBook, isDesignStage]);

  useEffect(() => {
    if (draftDialogOpen && !draftSourceArtifactId && preferredDraftSourceArtifact) {
      setDraftSourceArtifactId(preferredDraftSourceArtifact.id.toString());
    }
  }, [draftDialogOpen, draftSourceArtifactId, preferredDraftSourceArtifact]);
  const manuscriptParagraphs = useMemo(() => {
    return bookForm.manuscriptText
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }, [bookForm.manuscriptText]);
  const designArtifacts = useMemo<DesignArtifactRow[]>(() => {
    return [
      ...storyDocuments.map((artifact) => ({
        id: `story-document-${artifact.id}`,
        type: 'story_document',
        title: artifact.title,
        status: artifact.status,
        source: t('storyDocuments'),
      })),
      ...storyChapters.map((chapter) => ({
        id: `story-chapter-${chapter.id}`,
        type: 'story_chapter',
        title: chapter.title,
        status: chapter.status,
        source: t('chapters'),
      })),
      ...photoArtifacts.map((artifact) => ({
        id: `photo-${artifact.id}`,
        type: 'photo',
        title: artifact.title,
        status: artifact.status,
        source: t('photos'),
      })),
    ];
  }, [photoArtifacts, storyChapters, storyDocuments, t]);
  const paragraphAnchorOptions = manuscriptParagraphs.map((paragraph, index) => ({
    id: `paragraph-${index + 1}`,
    label: `${t('paragraph')} ${index + 1}`,
    preview: paragraph.slice(0, 48),
  }));
  const setField = (field: keyof BookDesignFormState, value: string | boolean) => {
    setBookForm((current) => ({ ...current, [field]: value }));
  };

  const setMatter = (key: 'front_matter' | 'content' | 'back_matter', units: LayoutUnit[]) => {
    setBookForm((current) => ({
      ...current,
      manuscript: { ...current.manuscript, [key]: units },
    }));
  };

  const renderBackgroundPhotoPicker = (
    field: 'defaultBackgroundAllArtifactId' | 'defaultBackgroundLeftArtifactId' | 'defaultBackgroundRightArtifactId',
    label: string
  ) => (
    <FormControl size="small" sx={{ minWidth: 220 }} disabled={photoArtifacts.length === 0}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={bookForm[field]} onChange={(event) => setField(field, event.target.value)}>
        <MenuItem value="">
          <em>{t('noPhoto')}</em>
        </MenuItem>
        {photoArtifacts.map((artifact) => (
          <MenuItem key={artifact.id} value={artifact.id.toString()}>
            {artifact.title || `#${artifact.id}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderBackgroundPreview = (artifactId: string) => {
    const id = Number(artifactId);
    if (!project || !id) {
      return null;
    }
    const url = platformApi.getProjectArtifactDownloadUrl(project.id, id);
    const placement = bookForm.defaultBackgroundPlacement;
    const objectFit = placement === 'contain' ? 'contain' : placement === 'stretch' ? 'fill' : 'cover';
    const commonSx = { width: 90, height: 120, border: '1px solid', borderColor: 'divider', borderRadius: 1 };
    if (placement === 'tile') {
      return <Box sx={{ ...commonSx, backgroundImage: `url("${url}")`, backgroundRepeat: 'repeat', backgroundSize: 'auto' }} />;
    }
    return <Box component="img" src={url} alt="" draggable={false} sx={{ ...commonSx, objectFit }} />;
  };

  const setBookType = (bookType: BookType) => {
    // Reseeds trim/margins for the chosen type (e.g. square page for picture
    // books); the manuscript's units are preserved.
    setBookForm((current) => applyBookTypeDefaults(current, bookType));
  };

  const loadDesignTemplates = useCallback(() => {
    if (designTemplates.length > 0 || templatesLoading) {
      return;
    }
    setTemplatesLoading(true);
    setTemplatesError(null);
    platformApi
      .listBookDesignTemplates()
      .then((templates) => setDesignTemplates(templates.filter((template) => template.is_active)))
      .catch(() => setTemplatesError(t('couldNotLoadDesignTemplates')))
      .finally(() => setTemplatesLoading(false));
  }, [designTemplates.length, t, templatesLoading]);

  const openTemplateDialog = () => {
    setTemplateAppliedNotice(false);
    setTemplateDialogOpen(true);
    loadDesignTemplates();
  };

  const openDraftDialog = () => {
    setDraftDialogError(null);
    if (!draftSourceArtifactId && preferredDraftSourceArtifact) {
      setDraftSourceArtifactId(preferredDraftSourceArtifact.id.toString());
    }
    setDraftDialogOpen(true);
    loadDesignTemplates();
  };

  const handleApplySelectedTemplate = () => {
    const template = designTemplates.find((item) => item.id.toString() === selectedTemplateId);
    if (!template) {
      return;
    }
    setBookForm((current) =>
      applyDesignSettingsToForm(current, template.design_settings_json, {
        language: template.language || undefined,
        textDirection: template.text_direction || undefined,
      })
    );
    setTemplateAppliedNotice(true);
  };

  useEffect(() => {
    if (draftDialogOpen) {
      loadDesignTemplates();
    }
  }, [draftDialogOpen, loadDesignTemplates]);

  const getPublishingDraftValue = (
    request: PublishingRequest,
    field: keyof PublishingRequestUpdatePayload
  ): string => {
    const draftValue = publishingDrafts[request.id]?.[field];
    if (typeof draftValue === 'string') {
      return draftValue;
    }
    const requestValue = request[field as keyof PublishingRequest];
    return typeof requestValue === 'string' ? requestValue : '';
  };

  const setPublishingDraftField = (
    request: PublishingRequest,
    field: keyof PublishingRequestUpdatePayload,
    value: string
  ) => {
    setPublishingDrafts((current) => ({
      ...current,
      [request.id]: {
        ...current[request.id],
        [field]: value,
      },
    }));
  };

  const savePublishingDraft = (
    request: PublishingRequest,
    overrides: PublishingRequestUpdatePayload = {}
  ) => {
    const draft = publishingDrafts[request.id] || {};
    onUpdatePublishingRequest(request, {
      ...draft,
      ...overrides,
    });
  };

  const setSpacingField = (field: keyof BookDesignFormState['spacing'], value: string) => {
    setBookForm((current) => ({
      ...current,
      spacing: {
        ...current.spacing,
        [field]: value,
      },
    }));
  };

  const setFontField = (
    role: keyof BookDesignFormState['fonts'],
    field: keyof BookDesignFormState['fonts'][keyof BookDesignFormState['fonts']],
    value: string | boolean
  ) => {
    setBookForm((current) => ({
      ...current,
      fonts: {
        ...current.fonts,
        [role]: {
          ...current.fonts[role],
          [field]: value,
        },
      },
    }));
  };

  const renderSettingTextField = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    type: 'text' | 'number' | 'color' = 'text'
  ) => (
    <TextField
      label={label}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      size="small"
      fullWidth
    />
  );

  const renderSettingSelect = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: Array<{ value: string; label: string }>
  ) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderSettingsTable = (rows: SettingsTableRow[]) => (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Table size="small">
        <TableHead
          sx={{
            bgcolor: '#f4f1ea',
            '& .MuiTableCell-root': {
              color: '#4a443a',
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
            },
          }}
        >
          <TableRow>
            <TableCell>{t('parameter')}</TableCell>
            <TableCell>{t('value')}</TableCell>
            <TableCell>{t('unit')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label} hover>
              <TableCell sx={{ width: '34%', fontWeight: 600 }}>{row.label}</TableCell>
              <TableCell>{row.value}</TableCell>
              <TableCell sx={{ width: 96, color: 'text.secondary' }}>{row.unit || ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderFontsTable = () => (
    <Stack spacing={1.5}>
      {bookFontRoles.map((role) => {
        const font = bookForm.fonts[role];
        const fontOptions = bookFontFamilyOptions.some((option) => option.value === font.family)
          ? bookFontFamilyOptions
          : [{ value: font.family, label: font.family, category: t('current') }, ...bookFontFamilyOptions];
        return (
          <Box
            key={role}
            sx={{
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: '#e0dbd2',
              borderRadius: 2,
              p: { xs: 1.5, md: 2 },
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={3}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {formatTranslatedLabel(t, role)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ direction: 'ltr', textAlign: 'start' }}>
                    {font.family}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel id={`font-family-${role}`}>{t('font')}</InputLabel>
                  <Select
                    labelId={`font-family-${role}`}
                    label={t('font')}
                    value={font.family}
                    onChange={(event) => setFontField(role, 'family', event.target.value)}
                    renderValue={(value) => (
                      <Typography sx={{ direction: 'ltr', fontFamily: `"${value}", "Noto Serif Hebrew", serif`, textAlign: 'start' }}>
                        {value}
                      </Typography>
                    )}
                  >
                    {fontOptions.map((option) => (
                      <MenuItem key={`${role}-${option.value}`} value={option.value}>
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography sx={{ direction: 'ltr', fontFamily: `"${option.value}", "Noto Serif Hebrew", serif` }}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.category}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={1.25}>
                {renderSettingTextField(t('size'), font.sizePt, (value) => setFontField(role, 'sizePt', value), 'number')}
              </Grid>
              <Grid item xs={6} sm={3} md={1.25}>
                {renderSettingTextField(t('lineHeight'), font.lineHeight, (value) => setFontField(role, 'lineHeight', value), 'number')}
              </Grid>
              <Grid item xs={6} sm={3} md={1.25}>
                {renderSettingTextField(t('color'), font.color, (value) => setFontField(role, 'color', value), 'color')}
              </Grid>
              <Grid item xs={12} md={12}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <Box
                      sx={{
                        border: '1px dashed',
                        borderColor: '#d8d0c4',
                        borderRadius: 1.5,
                        color: font.color,
                        fontFamily: `"${font.family}", "Noto Serif Hebrew", "Noto Sans Hebrew", serif`,
                        fontSize: `${Math.min(Math.max(Number(font.sizePt) || 11, 10), 24)}px`,
                        fontStyle: font.italic ? 'italic' : 'normal',
                        fontWeight: font.bold ? 700 : 400,
                        lineHeight: Number(font.lineHeight) || 1.4,
                        minHeight: 54,
                        overflow: 'hidden',
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      אבגדה וזכרונות משפחה · Talegence book preview
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      <FormControlLabel
                        control={<Switch checked={font.bold} onChange={(event) => setFontField(role, 'bold', event.target.checked)} />}
                        label={t('bold')}
                      />
                      <FormControlLabel
                        control={<Switch checked={font.italic} onChange={(event) => setFontField(role, 'italic', event.target.checked)} />}
                        label={t('italic')}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </Stack>
  );

  const renderParameterDialog = (
    open: boolean,
    onClose: () => void,
    title: string,
    content: React.ReactNode
  ) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Tune color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: '#e0dbd2',
            borderRadius: 2,
            overflowX: 'auto',
            p: { xs: 1, md: 1.5 },
          }}
        >
          {content}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
        <Button onClick={onClose}>{t('close')}</Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={() => {
            // Saving alone only persists the settings; the visible book is a
            // separately rendered artifact, so an existing draft must also
            // save and refresh its preview for the change to become visible.
            // onApplyDesignParameters surfaces an error instead of silently
            // re-rendering stale settings when the book is locked for editing.
            onApplyDesignParameters();
            onClose();
          }}
          disabled={saving || !currentBook}
        >
          {t('applyAndRefreshPreview')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const printParameterRows: SettingsTableRow[] = [
    {
      label: t('widthCm'),
      value: renderSettingTextField(t('widthCm'), bookForm.pageWidthCm, (value) => setField('pageWidthCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('heightCm'),
      value: renderSettingTextField(t('heightCm'), bookForm.pageHeightCm, (value) => setField('pageHeightCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('textAlign'),
      value: renderSettingSelect(t('textAlign'), bookForm.textAlignment, (value) => setField('textAlignment', value), [
        { value: 'left', label: formatTranslatedLabel(t, 'left') },
        { value: 'right', label: formatTranslatedLabel(t, 'right') },
        { value: 'center', label: formatTranslatedLabel(t, 'center') },
        { value: 'justify', label: formatTranslatedLabel(t, 'justify') },
      ]),
    },
    {
      label: t('startPage'),
      value: renderSettingTextField(t('startPage'), bookForm.startPageNumber, (value) => setField('startPageNumber', value), 'number'),
    },
    {
      label: t('innerMarginCm'),
      value: renderSettingTextField(t('innerMarginCm'), bookForm.marginInnerCm, (value) => setField('marginInnerCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('outerMarginCm'),
      value: renderSettingTextField(t('outerMarginCm'), bookForm.marginOuterCm, (value) => setField('marginOuterCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('topMarginCm'),
      value: renderSettingTextField(t('topMarginCm'), bookForm.marginTopCm, (value) => setField('marginTopCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('bottomMarginCm'),
      value: renderSettingTextField(t('bottomMarginCm'), bookForm.marginBottomCm, (value) => setField('marginBottomCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('rightPageHeader'),
      value: renderSettingTextField(t('rightPageHeader'), bookForm.rightHeaderContent, (value) => setField('rightHeaderContent', value)),
    },
    {
      label: t('leftPageHeader'),
      value: renderSettingTextField(t('leftPageHeader'), bookForm.leftHeaderContent, (value) => setField('leftHeaderContent', value)),
    },
    {
      label: t('rightHeaderTopCm'),
      value: renderSettingTextField(
        t('rightHeaderTopCm'),
        bookForm.rightHeaderTopMarginCm,
        (value) => setField('rightHeaderTopMarginCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('rightHeaderAlign'),
      value: renderSettingSelect(t('rightHeaderAlign'), bookForm.rightHeaderAlignment, (value) => setField('rightHeaderAlignment', value), [
        { value: 'outer', label: formatTranslatedLabel(t, 'outer') },
        { value: 'inner', label: formatTranslatedLabel(t, 'inner') },
        { value: 'center', label: formatTranslatedLabel(t, 'center') },
      ]),
    },
    {
      label: t('leftHeaderTopCm'),
      value: renderSettingTextField(
        t('leftHeaderTopCm'),
        bookForm.leftHeaderTopMarginCm,
        (value) => setField('leftHeaderTopMarginCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('leftHeaderAlign'),
      value: renderSettingSelect(t('leftHeaderAlign'), bookForm.leftHeaderAlignment, (value) => setField('leftHeaderAlignment', value), [
        { value: 'outer', label: formatTranslatedLabel(t, 'outer') },
        { value: 'inner', label: formatTranslatedLabel(t, 'inner') },
        { value: 'center', label: formatTranslatedLabel(t, 'center') },
      ]),
    },
  ];

  const indentationRows: SettingsTableRow[] = [
    {
      label: t('paragraphIndentCm'),
      value: renderSettingTextField(t('paragraphIndentCm'), bookForm.paragraphIndentCm, (value) => setField('paragraphIndentCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('subParagraphCm'),
      value: renderSettingTextField(
        t('subParagraphCm'),
        bookForm.subParagraphIndentCm,
        (value) => setField('subParagraphIndentCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('subSubParagraphCm'),
      value: renderSettingTextField(
        t('subSubParagraphCm'),
        bookForm.subSubParagraphIndentCm,
        (value) => setField('subSubParagraphIndentCm', value),
        'number'
      ),
      unit: t('cm'),
    },
  ];

  const tocRows: SettingsTableRow[] = [
    {
      label: t('generateContentIndex'),
      value: <Switch checked={bookForm.tocEnabled} onChange={(event) => setField('tocEnabled', event.target.checked)} />,
    },
    {
      label: t('contentIndexTitle'),
      value: renderSettingTextField(t('contentIndexTitle'), bookForm.tocTitle, (value) => setField('tocTitle', value)),
    },
    {
      label: t('includeSubChapters'),
      value: (
        <Switch
          checked={bookForm.tocIncludeSubChapters}
          onChange={(event) => setField('tocIncludeSubChapters', event.target.checked)}
          disabled={!bookForm.tocEnabled}
        />
      ),
    },
    {
      label: t('includeSubSubChapters'),
      value: (
        <Switch
          checked={bookForm.tocIncludeSubSubChapters}
          onChange={(event) => setField('tocIncludeSubSubChapters', event.target.checked)}
          disabled={!bookForm.tocEnabled}
        />
      ),
    },
    {
      label: t('contentIndexDotLeaders'),
      value: (
        <Switch
          checked={bookForm.tocDotLeaders}
          onChange={(event) => setField('tocDotLeaders', event.target.checked)}
          disabled={!bookForm.tocEnabled}
        />
      ),
    },
  ];

  const rulesRows: SettingsTableRow[] = [
    {
      label: t('chapterStart'),
      value: renderSettingSelect(t('chapterStart'), bookForm.chapterStartSide, (value) => setField('chapterStartSide', value), [
        { value: 'left', label: t('leftPage') },
        { value: 'right', label: t('rightPage') },
        { value: 'next', label: t('nextPage') },
      ]),
    },
    {
      label: t('chapterAboveCm'),
      value: renderSettingTextField(
        t('chapterAboveCm'),
        bookForm.spacing.chapterTitleBeforeCm,
        (value) => setSpacingField('chapterTitleBeforeCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('chapterBelowCm'),
      value: renderSettingTextField(
        t('chapterBelowCm'),
        bookForm.spacing.chapterTitleAfterCm,
        (value) => setSpacingField('chapterTitleAfterCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('subChapterAboveCm'),
      value: renderSettingTextField(
        t('subChapterAboveCm'),
        bookForm.spacing.subChapterBeforeCm,
        (value) => setSpacingField('subChapterBeforeCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('subChapterBelowCm'),
      value: renderSettingTextField(
        t('subChapterBelowCm'),
        bookForm.spacing.subChapterAfterCm,
        (value) => setSpacingField('subChapterAfterCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('subSubAboveCm'),
      value: renderSettingTextField(
        t('subSubAboveCm'),
        bookForm.spacing.subSubChapterBeforeCm,
        (value) => setSpacingField('subSubChapterBeforeCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('subSubBelowCm'),
      value: renderSettingTextField(
        t('subSubBelowCm'),
        bookForm.spacing.subSubChapterAfterCm,
        (value) => setSpacingField('subSubChapterAfterCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('textAboveCm'),
      value: renderSettingTextField(t('textAboveCm'), bookForm.spacing.textBeforeCm, (value) => setSpacingField('textBeforeCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('textBelowCm'),
      value: renderSettingTextField(t('textBelowCm'), bookForm.spacing.textAfterCm, (value) => setSpacingField('textAfterCm', value), 'number'),
      unit: t('cm'),
    },
    {
      label: t('paragraphEndMarker'),
      value: (
        <Switch
          checked={bookForm.paragraphEndMarkerEnabled}
          onChange={(event) => setField('paragraphEndMarkerEnabled', event.target.checked)}
        />
      ),
    },
    {
      label: t('marker'),
      value: renderSettingTextField(t('marker'), bookForm.paragraphEndMarker, (value) => setField('paragraphEndMarker', value)),
    },
    {
      label: t('markerOffsetCm'),
      value: renderSettingTextField(
        t('markerOffsetCm'),
        bookForm.paragraphEndMarkerOffsetCm,
        (value) => setField('paragraphEndMarkerOffsetCm', value),
        'number'
      ),
      unit: t('cm'),
    },
    {
      label: t('oneWord'),
      value: <Switch checked={bookForm.flagSingleWordLastLine} onChange={(event) => setField('flagSingleWordLastLine', event.target.checked)} />,
    },
    {
      label: t('oneLine'),
      value: <Switch checked={bookForm.flagSingleLinePage} onChange={(event) => setField('flagSingleLinePage', event.target.checked)} />,
    },
  ];

  const handleAddPhotoPlacement = () => {
    if (!bookForm.photoArtifactId) {
      return;
    }
    setBookForm((current) => ({
      ...current,
      imagePlacements: [
        ...current.imagePlacements,
        {
          id: `image-placement-${Date.now()}`,
          artifactId: current.photoArtifactId,
          anchorParagraphId: current.photoAnchorParagraphId || paragraphAnchorOptions[0]?.id || 'paragraph-1',
          placementMode: current.photoPlacementMode || 'after_paragraph',
          pageSide: current.photoPageSide || 'next',
          xPercent: current.photoXPercent || '50',
          yPercent: current.photoYPercent || '20',
          widthPercent: current.photoWidthPercent || '60',
          heightPercent: current.photoHeightPercent || '35',
          alignment: current.photoAlignment || 'center',
          rotationDegrees: current.photoRotationDegrees || '0',
          filter: current.photoFilter || 'none',
          cropTopPercent: current.photoCropTopPercent || '0',
          cropRightPercent: current.photoCropRightPercent || '0',
          cropBottomPercent: current.photoCropBottomPercent || '0',
          cropLeftPercent: current.photoCropLeftPercent || '0',
          caption: current.photoCaption,
        },
      ],
      photoCaption: '',
    }));
  };

  const handleRemovePhotoPlacement = (placementId: string) => {
    setBookForm((current) => ({
      ...current,
      imagePlacements: current.imagePlacements.filter((placement) => placement.id !== placementId),
    }));
  };

  const updatePhotoPlacement = (placementId: string, patch: Partial<BookImagePlacementFormState>) => {
    setBookForm((current) => ({
      ...current,
      imagePlacements: current.imagePlacements.map((placement) =>
        placement.id === placementId ? { ...placement, ...patch } : placement
      ),
    }));
  };

  const updatePositionFromDrop = (
    event: React.DragEvent<HTMLElement>,
    setter: (xPercent: string, yPercent: string) => void
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    setter(x.toFixed(1), y.toFixed(1));
  };

  const updateCropFromDrop = (
    event: React.DragEvent<HTMLElement>,
    edge: CropEdge,
    setter: (patch: Partial<Record<CropEdge, string>>) => void
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    if (edge === 'top') {
      setter({ top: y.toFixed(1) });
    } else if (edge === 'right') {
      setter({ right: (100 - x).toFixed(1) });
    } else if (edge === 'bottom') {
      setter({ bottom: (100 - y).toFixed(1) });
    } else {
      setter({ left: x.toFixed(1) });
    }
  };

  const renderCropHandles = (crop: Record<CropEdge, string>) => (
    <>
      <Box
        sx={{
          position: 'absolute',
          left: `${crop.left}%`,
          right: `${crop.right}%`,
          top: `${crop.top}%`,
          bottom: `${crop.bottom}%`,
          border: '1px dashed',
          borderColor: 'common.white',
          pointerEvents: 'none',
        }}
      />
      {cropEdges.map((edge) => (
        <Box
          key={edge}
          draggable
          onDragStart={(event) => {
            event.dataTransfer.setData('application/talegence-crop-edge', edge);
          }}
          sx={{
            position: 'absolute',
            left:
              edge === 'left'
                ? `${crop.left}%`
                : edge === 'right'
                  ? `calc(100% - ${crop.right}%)`
                  : '50%',
            top:
              edge === 'top'
                ? `${crop.top}%`
                : edge === 'bottom'
                  ? `calc(100% - ${crop.bottom}%)`
                  : '50%',
            width: 12,
            height: 12,
            bgcolor: 'common.white',
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: '50%',
            cursor: edge === 'left' || edge === 'right' ? 'ew-resize' : 'ns-resize',
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
    </>
  );

  const handleAddCoverItem = () => {
    if (bookForm.coverItemType === 'photo' && !bookForm.coverPhotoArtifactId) {
      return;
    }
    if (bookForm.coverItemType === 'text' && !bookForm.coverText.trim()) {
      return;
    }
    setBookForm((current) => ({
      ...current,
      coverItems: [
        ...current.coverItems,
        {
          id: `cover-item-${Date.now()}`,
          side: current.coverSide,
          itemType: current.coverItemType,
          artifactId: current.coverItemType === 'photo' ? current.coverPhotoArtifactId : '',
          text: current.coverItemType === 'text' ? current.coverText : '',
          xPercent: current.coverXPercent || '50',
          yPercent: current.coverYPercent || '50',
          widthPercent: current.coverWidthPercent || '70',
          heightPercent: current.coverHeightPercent || '50',
          rotationDegrees: current.coverRotationDegrees || '0',
          filter: current.coverFilter || 'none',
          cropTopPercent: current.coverCropTopPercent || '0',
          cropRightPercent: current.coverCropRightPercent || '0',
          cropBottomPercent: current.coverCropBottomPercent || '0',
          cropLeftPercent: current.coverCropLeftPercent || '0',
          fontFamily: current.coverItemType === 'text' ? current.coverFontFamily : '',
          fontSizePt: current.coverItemType === 'text' ? current.coverFontSizePt : '',
          color: current.coverItemType === 'text' ? current.coverColor : '',
          bold: current.coverBold,
          italic: current.coverItalic,
          textAlign: current.coverTextAlign || 'center',
        },
      ],
      coverText: '',
    }));
  };

  const handleRemoveCoverItem = (coverItemId: string) => {
    setBookForm((current) => ({
      ...current,
      coverItems: current.coverItems.filter((item) => item.id !== coverItemId),
    }));
  };

  const updateCoverItem = (coverItemId: string, patch: Partial<BookCoverItemFormState>) => {
    setBookForm((current) => ({
      ...current,
      coverItems: current.coverItems.map((item) => (item.id === coverItemId ? { ...item, ...patch } : item)),
    }));
  };

  const handleCreateDraft = async () => {
    const name = draftName.trim();
    if (!name) {
      setDraftDialogError(t('bookDesignDraftNameRequired'));
      return;
    }
    setDraftDialogError(null);
    const created = await onCreateBookDesignDraft({
      name,
      sourceArtifactId: draftSourceArtifactId ? Number(draftSourceArtifactId) : undefined,
      bookType: draftBookType,
      template: selectedDraftTemplate,
    });
    if (created) {
      setDraftName('');
      setDraftTemplateId('');
      setDraftDialogOpen(false);
    } else {
      setDraftDialogError(t('couldNotCreateBookDesignDraft'));
    }
  };

  const handleDeleteDraft = async (bookId: number) => {
    setDraftDialogError(null);
    const deleted = await onDeleteBookDesignDraft(bookId);
    if (!deleted) {
      setDraftDialogError(t('couldNotDeleteBookDesignDraft'));
    }
  };

  const designToolGroups = [
    {
      label: t('content'),
      icon: <MenuBook />,
      actions: [
        { label: t('content'), icon: <MenuBook />, onClick: () => setContentDialogOpen(true) },
        { label: t('contentIndex'), icon: <FactCheck />, onClick: () => setTocDialogOpen(true) },
        { label: t('bookDetails'), icon: <Article />, onClick: () => setDesignDetailsOpen(true) },
      ],
    },
    {
      label: t('visuals'),
      icon: <Palette />,
      actions: [
        { label: t('addPhoto'), icon: <AddPhotoAlternate />, onClick: () => setPhotoDialogOpen(true) },
        { label: t('coverDesign'), icon: <AutoStories />, onClick: () => setCoverDialogOpen(true) },
        { label: t('pageBackground'), icon: <Wallpaper />, onClick: () => setBackgroundDialogOpen(true) },
        { label: t('applyTemplate'), icon: <Palette />, onClick: openTemplateDialog },
      ],
    },
    {
      label: t('typography'),
      icon: <FormatSize />,
      actions: [
        { label: t('fonts'), icon: <FormatSize />, onClick: () => setFontsDialogOpen(true) },
        { label: t('indentations'), icon: <Tune />, onClick: () => setIndentDialogOpen(true) },
        { label: t('rules'), icon: <FactCheck />, onClick: () => setRulesDialogOpen(true) },
      ],
    },
    {
      label: t('printParameters'),
      icon: <Print />,
      actions: [
        { label: t('printParameters'), icon: <Print />, onClick: () => setPrintDialogOpen(true) },
        { label: t('refreshPreview'), icon: <Refresh />, onClick: onRefreshBookPreview, disabled: saving || !currentBook },
      ],
    },
  ];

  const dialogPaperSx = {
    borderRadius: 2,
    m: { xs: 1.5, sm: 3 },
    maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
    width: { xs: 'calc(100% - 24px)', sm: 'min(960px, calc(100% - 48px))' },
  };

  return (
    <Stack spacing={3}>
      <Paper
        variant="outlined"
        sx={{
          bgcolor: '#fbfaf7',
          borderColor: '#d9d5cc',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Grid container spacing={0} alignItems="stretch">
          <Grid item xs={12} md={isDesignStage ? 7 : 12}>
            {isDesignStage ? (
              <Stack spacing={1.25} sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<AutoStories />}
                    label={currentBook ? t('activeBookDesignDraft') : t('noActiveBookDesignDraft')}
                    color={currentBook ? 'primary' : 'default'}
                    variant={currentBook ? 'filled' : 'outlined'}
                    size="small"
                  />
                  {currentBook && (
                    <Chip
                      size="small"
                      label={formatTranslatedLabel(t, currentBook.status)}
                      color={getStatusColor(currentBook.status)}
                      variant="outlined"
                    />
                  )}
                  <Chip size="small" label={`${bookDesignDrafts.length} ${t('bookDesignDrafts')}`} variant="outlined" />
                </Stack>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {currentBook?.title || t('selectOrCreateBookDesignDraft')}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={formatTranslatedLabel(t, bookForm.bookType)} variant="outlined" />
                  <Chip size="small" label={selectedLanguage?.label || bookForm.language} variant="outlined" />
                  {previewPageCount > 0 && <Chip size="small" label={`${previewPageCount} ${t('pages')}`} variant="outlined" />}
                </Stack>
              </Stack>
            ) : (
              <Box sx={{ p: 2 }}>
                <FormControl fullWidth disabled={books.length === 0}>
                  <InputLabel id="book-select-label">{t('book')}</InputLabel>
                  <Select
                    labelId="book-select-label"
                    label={t('book')}
                    value={selectedBookId}
                    onChange={(event) => onSelectBook(event.target.value)}
                  >
                    {books.map((book) => (
                      <MenuItem key={book.id} value={book.id.toString()}>
                        {book.title} (#{book.id} · {formatTranslatedLabel(t, book.status)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Grid>
          {isDesignStage && (
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                borderTop: { xs: '1px solid', md: 0 },
                borderInlineStart: { md: '1px solid' },
                borderColor: '#e2ded6',
                bgcolor: '#fff',
              }}
            >
              <Stack
                spacing={1.25}
                alignItems={{ xs: 'stretch', md: 'flex-end' }}
                sx={{ p: { xs: 2, md: 2.5 } }}
              >
                <Button startIcon={<MenuBook />} variant="outlined" onClick={openDraftDialog} sx={{ alignSelf: { xs: 'flex-start', md: 'flex-end' } }}>
                  {t('bookDesignDrafts')}
                </Button>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                  <Button startIcon={<Save />} variant="contained" onClick={onSaveBook} disabled={saving || !currentBook}>
                  {t('saveBook')}
                  </Button>
                  <Button startIcon={<AutoStories />} variant="outlined" onClick={onSaveBookVersion} disabled={saving || !currentBook}>
                    {t('saveVersion')}
                  </Button>
                  <Button
                    startIcon={<AssignmentTurnedIn />}
                    color="success"
                    variant="outlined"
                    onClick={onApproveBook}
                    disabled={saving || !currentBook || currentBook.status === 'approved_for_export'}
                  >
                    {t('approveBook')}
                  </Button>
                </Stack>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Paper>

      {(!isDesignStage || currentBook) && (
        <Grid
          container
          spacing={3}
          alignItems="flex-start"
          sx={{ flexDirection: { lg: bookForm.textDirection === 'rtl' ? 'row-reverse' : 'row' } }}
        >
        {isDesignStage && (
          <Grid item xs={12} lg={12}>
            <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2, display: 'none' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <Article />
                <Typography variant="h6">{t('designWorkspace')}</Typography>
                {currentBook && (
                  <Chip size="small" label={formatTranslatedLabel(t, currentBook.status)} color={getStatusColor(currentBook.status)} />
                )}
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('title')}
                    value={bookForm.title}
                    onChange={(event) => setField('title', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('subtitle')}
                    value={bookForm.subtitle}
                    onChange={(event) => setField('subtitle', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('authorName')}
                    value={bookForm.authorName}
                    onChange={(event) => setField('authorName', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="book-language-label">{t('language')}</InputLabel>
                    <Select
                      labelId="book-language-label"
                      label={t('language')}
                      value={bookForm.language}
                      onChange={(event) => {
                        const language = languageOptions.find((option) => option.value === event.target.value);
                        setBookForm((current) => ({
                          ...current,
                          language: event.target.value,
                          textDirection: language?.direction || current.textDirection,
                          textAlignment: language?.direction === 'rtl' ? 'right' : current.textAlignment,
                        }));
                      }}
                    >
                      {languageOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="text-direction-label">{t('direction')}</InputLabel>
                    <Select
                      labelId="text-direction-label"
                      label={t('direction')}
                      value={bookForm.textDirection}
                      onChange={(event) => setField('textDirection', event.target.value)}
                    >
                      <MenuItem value="ltr">{t('leftToRight')}</MenuItem>
                      <MenuItem value="rtl">{t('rightToLeft')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" icon={<Article />} label={`${storyDocuments.length} ${t('storyDocuments')}`} variant="outlined" />
                    <Chip size="small" icon={<AutoStories />} label={`${storyChapters.length} ${t('chapters')}`} variant="outlined" />
                    <Chip size="small" icon={<Image />} label={`${photoArtifacts.length} ${t('photos')}`} variant="outlined" />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('type')}</TableCell>
                          <TableCell>{t('title')}</TableCell>
                          <TableCell>{t('source')}</TableCell>
                          <TableCell>{t('status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {designArtifacts.map((artifact) => (
                          <TableRow key={artifact.id} hover>
                            <TableCell>{formatTranslatedLabel(t, artifact.type)}</TableCell>
                            <TableCell>{artifact.title}</TableCell>
                            <TableCell>{artifact.source}</TableCell>
                            <TableCell>
                              <Chip size="small" label={formatTranslatedLabel(t, artifact.status)} color={getStatusColor(artifact.status)} />
                            </TableCell>
                          </TableRow>
                        ))}
                        {designArtifacts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                {t('noDesignArtifactsYet')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'none' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <AddPhotoAlternate />
                <Typography variant="h6">{t('photos')}</Typography>
                <Chip size="small" label={`${bookForm.imagePlacements.length} ${t('placed')}`} variant="outlined" />
              </Stack>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={photoArtifacts.length === 0}>
                    <InputLabel id="photo-artifact-label">{t('photo')}</InputLabel>
                    <Select
                      labelId="photo-artifact-label"
                      label={t('photo')}
                      value={bookForm.photoArtifactId}
                      onChange={(event) => setField('photoArtifactId', event.target.value)}
                    >
                      {photoArtifacts.map((artifact) => (
                        <MenuItem key={artifact.id} value={artifact.id.toString()}>
                          {artifact.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={paragraphAnchorOptions.length === 0}>
                    <InputLabel id="photo-anchor-label">{t('anchor')}</InputLabel>
                    <Select
                      labelId="photo-anchor-label"
                      label={t('anchor')}
                      value={bookForm.photoAnchorParagraphId}
                      onChange={(event) => setField('photoAnchorParagraphId', event.target.value)}
                    >
                      {paragraphAnchorOptions.map((paragraph) => (
                        <MenuItem key={paragraph.id} value={paragraph.id}>
                          {paragraph.label}: {paragraph.preview}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="photo-placement-mode-label">{t('placement')}</InputLabel>
                    <Select
                      labelId="photo-placement-mode-label"
                      label={t('placement')}
                      value={bookForm.photoPlacementMode}
                      onChange={(event) => setField('photoPlacementMode', event.target.value)}
                    >
                      <MenuItem value="after_paragraph">{formatTranslatedLabel(t, 'after_paragraph')}</MenuItem>
                      <MenuItem value="top_of_next_page">{formatTranslatedLabel(t, 'top_of_next_page')}</MenuItem>
                      <MenuItem value="full_page">{formatTranslatedLabel(t, 'full_page')}</MenuItem>
                      <MenuItem value="absolute">{formatTranslatedLabel(t, 'absolute')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="photo-page-side-label">{t('pageSide')}</InputLabel>
                    <Select
                      labelId="photo-page-side-label"
                      label={t('pageSide')}
                      value={bookForm.photoPageSide}
                      onChange={(event) => setField('photoPageSide', event.target.value)}
                    >
                      <MenuItem value="next">{formatTranslatedLabel(t, 'next')}</MenuItem>
                      <MenuItem value="left">{formatTranslatedLabel(t, 'left')}</MenuItem>
                      <MenuItem value="right">{formatTranslatedLabel(t, 'right')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('xPercent')}
                    type="number"
                    value={bookForm.photoXPercent}
                    onChange={(event) => setField('photoXPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('yPercent')}
                    type="number"
                    value={bookForm.photoYPercent}
                    onChange={(event) => setField('photoYPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('widthPercent')}
                    type="number"
                    value={bookForm.photoWidthPercent}
                    onChange={(event) => setField('photoWidthPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('heightPercent')}
                    type="number"
                    value={bookForm.photoHeightPercent}
                    onChange={(event) => setField('photoHeightPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="photo-align-label">{t('align')}</InputLabel>
                    <Select
                      labelId="photo-align-label"
                      label={t('align')}
                      value={bookForm.photoAlignment}
                      onChange={(event) => setField('photoAlignment', event.target.value)}
                    >
                      <MenuItem value="left">{formatTranslatedLabel(t, 'left')}</MenuItem>
                      <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                      <MenuItem value="right">{formatTranslatedLabel(t, 'right')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('rotate')}
                    type="number"
                    value={bookForm.photoRotationDegrees}
                    onChange={(event) => setField('photoRotationDegrees', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="photo-filter-label">{t('filter')}</InputLabel>
                    <Select
                      labelId="photo-filter-label"
                      label={t('filter')}
                      value={bookForm.photoFilter}
                      onChange={(event) => setField('photoFilter', event.target.value)}
                    >
                      <MenuItem value="none">{t('none')}</MenuItem>
                      <MenuItem value="grayscale">{formatTranslatedLabel(t, 'grayscale')}</MenuItem>
                      <MenuItem value="sepia">{formatTranslatedLabel(t, 'sepia')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropTopPercent')}
                    type="number"
                    value={bookForm.photoCropTopPercent}
                    onChange={(event) => setField('photoCropTopPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropRightPercent')}
                    type="number"
                    value={bookForm.photoCropRightPercent}
                    onChange={(event) => setField('photoCropRightPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropBottomPercent')}
                    type="number"
                    value={bookForm.photoCropBottomPercent}
                    onChange={(event) => setField('photoCropBottomPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropLeftPercent')}
                    type="number"
                    value={bookForm.photoCropLeftPercent}
                    onChange={(event) => setField('photoCropLeftPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    label={t('caption')}
                    value={bookForm.photoCaption}
                    onChange={(event) => setField('photoCaption', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Box
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault();
                      const cropEdge = event.dataTransfer.getData('application/talegence-crop-edge');
                      if (isCropEdge(cropEdge)) {
                        updateCropFromDrop(event, cropEdge, (patch) => {
                          setBookForm((current) => ({
                            ...current,
                            photoCropTopPercent: patch.top ?? current.photoCropTopPercent,
                            photoCropRightPercent: patch.right ?? current.photoCropRightPercent,
                            photoCropBottomPercent: patch.bottom ?? current.photoCropBottomPercent,
                            photoCropLeftPercent: patch.left ?? current.photoCropLeftPercent,
                          }));
                        });
                        return;
                      }
                      updatePositionFromDrop(event, (xPercent, yPercent) => {
                        setBookForm((current) => ({
                          ...current,
                          photoXPercent: xPercent,
                          photoYPercent: yPercent,
                        }));
                      });
                    }}
                    sx={{
                      position: 'relative',
                      height: 150,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      draggable
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const cropEdge = event.dataTransfer.getData('application/talegence-crop-edge');
                        if (!isCropEdge(cropEdge)) {
                          return;
                        }
                        updateCropFromDrop(event, cropEdge, (patch) => {
                          setBookForm((current) => ({
                            ...current,
                            photoCropTopPercent: patch.top ?? current.photoCropTopPercent,
                            photoCropRightPercent: patch.right ?? current.photoCropRightPercent,
                            photoCropBottomPercent: patch.bottom ?? current.photoCropBottomPercent,
                            photoCropLeftPercent: patch.left ?? current.photoCropLeftPercent,
                          }));
                        });
                      }}
                      sx={{
                        position: 'absolute',
                        left: `${bookForm.photoXPercent}%`,
                        top: `${bookForm.photoYPercent}%`,
                        width: `${bookForm.photoWidthPercent}%`,
                        height: `${bookForm.photoHeightPercent}%`,
                        transform: `translate(-50%, -50%) rotate(${bookForm.photoRotationDegrees}deg)`,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        bgcolor: 'primary.light',
                        opacity: 0.45,
                        cursor: 'grab',
                      }}
                    >
                      {renderCropHandles({
                        top: bookForm.photoCropTopPercent,
                        right: bookForm.photoCropRightPercent,
                        bottom: bookForm.photoCropBottomPercent,
                        left: bookForm.photoCropLeftPercent,
                      })}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    startIcon={<AddPhotoAlternate />}
                    onClick={handleAddPhotoPlacement}
                    disabled={!bookForm.photoArtifactId}
                    fullWidth
                  >
                    {t('addPhoto')}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('photo')}</TableCell>
                          <TableCell>{t('anchor')}</TableCell>
                          <TableCell>{t('layout')}</TableCell>
                          <TableCell>{t('caption')}</TableCell>
                          <TableCell align="right">{t('actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bookForm.imagePlacements.map((placement) => {
                          const artifact = photoArtifacts.find((item) => item.id.toString() === placement.artifactId);
                          return (
                            <TableRow key={placement.id} hover>
                              <TableCell>{artifact?.title || `Photo #${placement.artifactId}`}</TableCell>
                              <TableCell>{placement.anchorParagraphId}</TableCell>
                              <TableCell>
                                {formatTranslatedLabel(t, placement.placementMode)}, {placement.widthPercent}% x {placement.heightPercent}%,{' '}
                                {placement.xPercent}/{placement.yPercent}, {placement.rotationDegrees} deg
                              </TableCell>
                              <TableCell>{placement.caption || t('none')}</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  aria-label={t('movePhotoToCurrentPosition')}
                                  onClick={() =>
                                    updatePhotoPlacement(placement.id, {
                                      placementMode: bookForm.photoPlacementMode,
                                      pageSide: bookForm.photoPageSide,
                                      xPercent: bookForm.photoXPercent,
                                      yPercent: bookForm.photoYPercent,
                                      widthPercent: bookForm.photoWidthPercent,
                                      heightPercent: bookForm.photoHeightPercent,
                                      rotationDegrees: bookForm.photoRotationDegrees,
                                      filter: bookForm.photoFilter,
                                      cropTopPercent: bookForm.photoCropTopPercent,
                                      cropRightPercent: bookForm.photoCropRightPercent,
                                      cropBottomPercent: bookForm.photoCropBottomPercent,
                                      cropLeftPercent: bookForm.photoCropLeftPercent,
                                    })
                                  }
                                  size="small"
                                >
                                  <Save />
                                </IconButton>
                                <IconButton
                                  aria-label={t('removePhoto')}
                                  onClick={() => handleRemovePhotoPlacement(placement.id)}
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {bookForm.imagePlacements.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                {t('noPhotosPlacedYet')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'none' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <AutoStories />
                <Typography variant="h6">{t('cover')}</Typography>
                <Chip size="small" label={`${bookForm.coverItems.length} ${t('items')}`} variant="outlined" />
              </Stack>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="cover-side-label">{t('side')}</InputLabel>
                    <Select
                      labelId="cover-side-label"
                      label={t('side')}
                      value={bookForm.coverSide}
                      onChange={(event) => setBookForm((current) => ({ ...current, coverSide: event.target.value as 'front' | 'back' }))}
                    >
                      <MenuItem value="front">{formatTranslatedLabel(t, 'front')}</MenuItem>
                      <MenuItem value="back">{formatTranslatedLabel(t, 'back')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="cover-item-type-label">{t('type')}</InputLabel>
                    <Select
                      labelId="cover-item-type-label"
                      label={t('type')}
                      value={bookForm.coverItemType}
                      onChange={(event) =>
                        setBookForm((current) => ({ ...current, coverItemType: event.target.value as 'photo' | 'text' }))
                      }
                    >
                      <MenuItem value="photo">{t('photo')}</MenuItem>
                      <MenuItem value="text">{formatTranslatedLabel(t, 'text')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  {bookForm.coverItemType === 'photo' ? (
                    <FormControl fullWidth disabled={photoArtifacts.length === 0}>
                      <InputLabel id="cover-photo-label">{t('photo')}</InputLabel>
                      <Select
                        labelId="cover-photo-label"
                        label={t('photo')}
                        value={bookForm.coverPhotoArtifactId}
                        onChange={(event) => setField('coverPhotoArtifactId', event.target.value)}
                      >
                        {photoArtifacts.map((artifact) => (
                          <MenuItem key={artifact.id} value={artifact.id.toString()}>
                            {artifact.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      label={t('coverText')}
                      value={bookForm.coverText}
                      onChange={(event) => setField('coverText', event.target.value)}
                      fullWidth
                    />
                  )}
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('xPercent')}
                    type="number"
                    value={bookForm.coverXPercent}
                    onChange={(event) => setField('coverXPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('yPercent')}
                    type="number"
                    value={bookForm.coverYPercent}
                    onChange={(event) => setField('coverYPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('widthPercent')}
                    type="number"
                    value={bookForm.coverWidthPercent}
                    onChange={(event) => setField('coverWidthPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('heightPercent')}
                    type="number"
                    value={bookForm.coverHeightPercent}
                    onChange={(event) => setField('coverHeightPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('rotate')}
                    type="number"
                    value={bookForm.coverRotationDegrees}
                    onChange={(event) => setField('coverRotationDegrees', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="cover-filter-label">{t('filter')}</InputLabel>
                    <Select
                      labelId="cover-filter-label"
                      label={t('filter')}
                      value={bookForm.coverFilter}
                      onChange={(event) => setField('coverFilter', event.target.value)}
                    >
                      <MenuItem value="none">{t('none')}</MenuItem>
                      <MenuItem value="grayscale">{formatTranslatedLabel(t, 'grayscale')}</MenuItem>
                      <MenuItem value="sepia">{formatTranslatedLabel(t, 'sepia')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropTopPercent')}
                    type="number"
                    value={bookForm.coverCropTopPercent}
                    onChange={(event) => setField('coverCropTopPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropRightPercent')}
                    type="number"
                    value={bookForm.coverCropRightPercent}
                    onChange={(event) => setField('coverCropRightPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropBottomPercent')}
                    type="number"
                    value={bookForm.coverCropBottomPercent}
                    onChange={(event) => setField('coverCropBottomPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('cropLeftPercent')}
                    type="number"
                    value={bookForm.coverCropLeftPercent}
                    onChange={(event) => setField('coverCropLeftPercent', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Box
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault();
                      const cropEdge = event.dataTransfer.getData('application/talegence-crop-edge');
                      if (isCropEdge(cropEdge)) {
                        updateCropFromDrop(event, cropEdge, (patch) => {
                          setBookForm((current) => ({
                            ...current,
                            coverCropTopPercent: patch.top ?? current.coverCropTopPercent,
                            coverCropRightPercent: patch.right ?? current.coverCropRightPercent,
                            coverCropBottomPercent: patch.bottom ?? current.coverCropBottomPercent,
                            coverCropLeftPercent: patch.left ?? current.coverCropLeftPercent,
                          }));
                        });
                        return;
                      }
                      updatePositionFromDrop(event, (xPercent, yPercent) => {
                        setBookForm((current) => ({
                          ...current,
                          coverXPercent: xPercent,
                          coverYPercent: yPercent,
                        }));
                      });
                    }}
                    sx={{
                      position: 'relative',
                      height: 180,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      draggable
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const cropEdge = event.dataTransfer.getData('application/talegence-crop-edge');
                        if (!isCropEdge(cropEdge)) {
                          return;
                        }
                        updateCropFromDrop(event, cropEdge, (patch) => {
                          setBookForm((current) => ({
                            ...current,
                            coverCropTopPercent: patch.top ?? current.coverCropTopPercent,
                            coverCropRightPercent: patch.right ?? current.coverCropRightPercent,
                            coverCropBottomPercent: patch.bottom ?? current.coverCropBottomPercent,
                            coverCropLeftPercent: patch.left ?? current.coverCropLeftPercent,
                          }));
                        });
                      }}
                      sx={{
                        position: 'absolute',
                        left: `${bookForm.coverXPercent}%`,
                        top: `${bookForm.coverYPercent}%`,
                        width: `${bookForm.coverWidthPercent}%`,
                        height: `${bookForm.coverHeightPercent}%`,
                        transform: `translate(-50%, -50%) rotate(${bookForm.coverRotationDegrees}deg)`,
                        border: '2px solid',
                        borderColor: 'secondary.main',
                        bgcolor: 'secondary.light',
                        opacity: 0.45,
                        cursor: 'grab',
                      }}
                    >
                      {renderCropHandles({
                        top: bookForm.coverCropTopPercent,
                        right: bookForm.coverCropRightPercent,
                        bottom: bookForm.coverCropBottomPercent,
                        left: bookForm.coverCropLeftPercent,
                      })}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    startIcon={<Add />}
                    onClick={handleAddCoverItem}
                    disabled={
                      (bookForm.coverItemType === 'photo' && !bookForm.coverPhotoArtifactId) ||
                      (bookForm.coverItemType === 'text' && !bookForm.coverText.trim())
                    }
                    fullWidth
                  >
                    {t('addCover')}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('side')}</TableCell>
                          <TableCell>{t('item')}</TableCell>
                          <TableCell>{t('position')}</TableCell>
                          <TableCell align="right">{t('actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bookForm.coverItems.map((item) => {
                          const artifact = photoArtifacts.find((photo) => photo.id.toString() === item.artifactId);
                          return (
                            <TableRow key={item.id} hover>
                              <TableCell>{formatTranslatedLabel(t, item.side)}</TableCell>
                              <TableCell>{item.itemType === 'photo' ? artifact?.title || `Photo #${item.artifactId}` : item.text}</TableCell>
                              <TableCell>
                                {item.xPercent}/{item.yPercent}, {item.widthPercent}% x {item.heightPercent}%, {item.rotationDegrees} deg
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  aria-label={t('moveCoverItemToCurrentPosition')}
                                  onClick={() =>
                                    updateCoverItem(item.id, {
                                      xPercent: bookForm.coverXPercent,
                                      yPercent: bookForm.coverYPercent,
                                      widthPercent: bookForm.coverWidthPercent,
                                      heightPercent: bookForm.coverHeightPercent,
                                      rotationDegrees: bookForm.coverRotationDegrees,
                                      filter: bookForm.coverFilter,
                                      cropTopPercent: bookForm.coverCropTopPercent,
                                      cropRightPercent: bookForm.coverCropRightPercent,
                                      cropBottomPercent: bookForm.coverCropBottomPercent,
                                      cropLeftPercent: bookForm.coverCropLeftPercent,
                                    })
                                  }
                                  size="small"
                                >
                                  <Save />
                                </IconButton>
                                <IconButton
                                  aria-label={t('removeCoverItem')}
                                  onClick={() => handleRemoveCoverItem(item.id)}
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {bookForm.coverItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                {t('noCoverItemsYet')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Paper>


            <Paper variant="outlined" sx={{ p: 2, display: 'none' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <FactCheck />
                <Typography variant="h6">{t('printParameters')}</Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('widthCm')}
                    type="number"
                    value={bookForm.pageWidthCm}
                    onChange={(event) => setField('pageWidthCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('heightCm')}
                    type="number"
                    value={bookForm.pageHeightCm}
                    onChange={(event) => setField('pageHeightCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="text-align-label">{t('textAlign')}</InputLabel>
                    <Select
                      labelId="text-align-label"
                      label={t('textAlign')}
                      value={bookForm.textAlignment}
                      onChange={(event) => setField('textAlignment', event.target.value)}
                    >
                      <MenuItem value="left">{formatTranslatedLabel(t, 'left')}</MenuItem>
                      <MenuItem value="right">{formatTranslatedLabel(t, 'right')}</MenuItem>
                      <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                      <MenuItem value="justify">{formatTranslatedLabel(t, 'justify')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('startPage')}
                    type="number"
                    value={bookForm.startPageNumber}
                    onChange={(event) => setField('startPageNumber', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('innerMarginCm')}
                    type="number"
                    value={bookForm.marginInnerCm}
                    onChange={(event) => setField('marginInnerCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('outerMarginCm')}
                    type="number"
                    value={bookForm.marginOuterCm}
                    onChange={(event) => setField('marginOuterCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('topMarginCm')}
                    type="number"
                    value={bookForm.marginTopCm}
                    onChange={(event) => setField('marginTopCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('bottomMarginCm')}
                    type="number"
                    value={bookForm.marginBottomCm}
                    onChange={(event) => setField('marginBottomCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('rightPageHeader')}
                    value={bookForm.rightHeaderContent}
                    onChange={(event) => setField('rightHeaderContent', event.target.value)}
                    helperText={t('headerTokensHint')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('leftPageHeader')}
                    value={bookForm.leftHeaderContent}
                    onChange={(event) => setField('leftHeaderContent', event.target.value)}
                    helperText={t('headerTokensHint')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('rightHeaderTopCm')}
                    type="number"
                    value={bookForm.rightHeaderTopMarginCm}
                    onChange={(event) => setField('rightHeaderTopMarginCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="right-header-align-label">{t('rightHeaderAlign')}</InputLabel>
                    <Select
                      labelId="right-header-align-label"
                      label={t('rightHeaderAlign')}
                      value={bookForm.rightHeaderAlignment}
                      onChange={(event) => setField('rightHeaderAlignment', event.target.value)}
                    >
                      <MenuItem value="outer">{formatTranslatedLabel(t, 'outer')}</MenuItem>
                      <MenuItem value="inner">{formatTranslatedLabel(t, 'inner')}</MenuItem>
                      <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={t('leftHeaderTopCm')}
                    type="number"
                    value={bookForm.leftHeaderTopMarginCm}
                    onChange={(event) => setField('leftHeaderTopMarginCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="left-header-align-label">{t('leftHeaderAlign')}</InputLabel>
                    <Select
                      labelId="left-header-align-label"
                      label={t('leftHeaderAlign')}
                      value={bookForm.leftHeaderAlignment}
                      onChange={(event) => setField('leftHeaderAlignment', event.target.value)}
                    >
                      <MenuItem value="outer">{formatTranslatedLabel(t, 'outer')}</MenuItem>
                      <MenuItem value="inner">{formatTranslatedLabel(t, 'inner')}</MenuItem>
                      <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'none' }}>
              <Typography variant="h6" mb={2}>
                {t('typography')}
              </Typography>
              <Grid container spacing={2}>
                {bookFontRoles.map((role) => {
                  const font = bookForm.fonts[role];
                  return (
                    <Grid key={role} item xs={12}>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} md={2}>
                          <Typography fontWeight={700}>{bookFontLabels[role]}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
	                            label={t('font')}
                            value={font.family}
                            onChange={(event) =>
                              setBookForm((current) => ({
                                ...current,
                                fonts: {
                                  ...current.fonts,
                                  [role]: { ...font, family: event.target.value },
                                },
                              }))
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                          <TextField
	                            label={t('size')}
                            type="number"
                            value={font.sizePt}
                            onChange={(event) =>
                              setBookForm((current) => ({
                                ...current,
                                fonts: {
                                  ...current.fonts,
                                  [role]: { ...font, sizePt: event.target.value },
                                },
                              }))
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                          <TextField
	                            label={t('color')}
                            type="color"
                            value={font.color}
                            onChange={(event) =>
                              setBookForm((current) => ({
                                ...current,
                                fonts: {
                                  ...current.fonts,
                                  [role]: { ...font, color: event.target.value },
                                },
                              }))
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={font.bold}
                                onChange={(event) =>
                                  setBookForm((current) => ({
                                    ...current,
                                    fonts: {
                                      ...current.fonts,
                                      [role]: { ...font, bold: event.target.checked },
                                    },
                                  }))
                                }
                              />
                            }
	                            label={t('bold')}
                          />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={font.italic}
                                onChange={(event) =>
                                  setBookForm((current) => ({
                                    ...current,
                                    fonts: {
                                      ...current.fonts,
                                      [role]: { ...font, italic: event.target.checked },
                                    },
                                  }))
                                }
                              />
                            }
	                            label={t('italic')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'none' }}>
              <Typography variant="h6" mb={2}>
	                {t('spacingAndRules')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('paragraphIndentCm')}
                    type="number"
                    value={bookForm.paragraphIndentCm}
                    onChange={(event) => setField('paragraphIndentCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('subParagraphCm')}
                    type="number"
                    value={bookForm.subParagraphIndentCm}
                    onChange={(event) => setField('subParagraphIndentCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('subSubParagraphCm')}
                    type="number"
                    value={bookForm.subSubParagraphIndentCm}
                    onChange={(event) => setField('subSubParagraphIndentCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth>
	                    <InputLabel id="chapter-start-side-label">{t('chapterStart')}</InputLabel>
                    <Select
                      labelId="chapter-start-side-label"
	                      label={t('chapterStart')}
                      value={bookForm.chapterStartSide}
                      onChange={(event) => setField('chapterStartSide', event.target.value)}
                    >
	                      <MenuItem value="left">{t('leftPage')}</MenuItem>
	                      <MenuItem value="right">{t('rightPage')}</MenuItem>
	                      <MenuItem value="next">{t('nextPage')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('chapterAboveCm')}
                    type="number"
                    value={bookForm.spacing.chapterTitleBeforeCm}
                    onChange={(event) => setSpacingField('chapterTitleBeforeCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('chapterBelowCm')}
                    type="number"
                    value={bookForm.spacing.chapterTitleAfterCm}
                    onChange={(event) => setSpacingField('chapterTitleAfterCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('subChapterAboveCm')}
                    type="number"
                    value={bookForm.spacing.subChapterBeforeCm}
                    onChange={(event) => setSpacingField('subChapterBeforeCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('subChapterBelowCm')}
                    type="number"
                    value={bookForm.spacing.subChapterAfterCm}
                    onChange={(event) => setSpacingField('subChapterAfterCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('subSubAboveCm')}
                    type="number"
                    value={bookForm.spacing.subSubChapterBeforeCm}
                    onChange={(event) => setSpacingField('subSubChapterBeforeCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('subSubBelowCm')}
                    type="number"
                    value={bookForm.spacing.subSubChapterAfterCm}
                    onChange={(event) => setSpacingField('subSubChapterAfterCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('textAboveCm')}
                    type="number"
                    value={bookForm.spacing.textBeforeCm}
                    onChange={(event) => setSpacingField('textBeforeCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
	                    label={t('textBelowCm')}
                    type="number"
                    value={bookForm.spacing.textAfterCm}
                    onChange={(event) => setSpacingField('textAfterCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bookForm.paragraphEndMarkerEnabled}
                        onChange={(event) => setField('paragraphEndMarkerEnabled', event.target.checked)}
                      />
                    }
	                    label={t('paragraphEndMarker')}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
	                    label={t('marker')}
                    value={bookForm.paragraphEndMarker}
                    onChange={(event) => setField('paragraphEndMarker', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
	                    label={t('markerOffsetCm')}
                    type="number"
                    value={bookForm.paragraphEndMarkerOffsetCm}
                    onChange={(event) => setField('paragraphEndMarkerOffsetCm', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bookForm.flagSingleWordLastLine}
                        onChange={(event) => setField('flagSingleWordLastLine', event.target.checked)}
                      />
                    }
	                    label={t('oneWord')}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bookForm.flagSingleLinePage}
                        onChange={(event) => setField('flagSingleLinePage', event.target.checked)}
                      />
                    }
	                    label={t('oneLine')}
                  />
                </Grid>
              </Grid>
            </Paper>
            </Stack>
          </Grid>
        )}

        <Grid item xs={12} lg={12}>
          <Stack spacing={3}>
            {isDesignStage && (
              <Paper
                variant="outlined"
                sx={{
                  bgcolor: '#ffffff',
                  borderColor: '#ded9d0',
                  borderRadius: 2,
                  p: { xs: 1.5, md: 2 },
                }}
              >
                <Grid container spacing={1.5}>
                  {designToolGroups.map((group) => (
                    <Grid item xs={12} md={6} xl={3} key={group.label}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: '#e5e0d8',
                          borderRadius: 2,
                          height: { xs: 'auto', md: '100%' },
                          p: 1.25,
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                alignItems: 'center',
                                bgcolor: 'rgba(42, 92, 87, 0.1)',
                                borderRadius: 1.5,
                                color: 'primary.main',
                                display: 'flex',
                                height: 34,
                                justifyContent: 'center',
                                width: 34,
                                '& .MuiSvgIcon-root': { fontSize: 20 },
                              }}
                            >
                              {group.icon}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {group.label}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {group.actions.map((action) => (
                              <Button
                                key={action.label}
                                startIcon={action.icon}
                                variant="text"
                                size="small"
                                onClick={action.onClick}
                                disabled={action.disabled}
                                sx={{
                                  border: '1px solid',
                                  borderColor: '#ebe6dd',
                                  color: 'text.primary',
                                  minHeight: 34,
                                  px: 1.25,
                                  '&:hover': {
                                    bgcolor: '#f6f2ea',
                                    borderColor: '#d8d0c4',
                                  },
                                }}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </Stack>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
            {isDesignStage && (
              <Paper
                variant="outlined"
                sx={{
                  bgcolor: '#fffdfa',
                  borderColor: '#d8d2c7',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    bgcolor: '#25231f',
                    borderBottom: '1px solid',
                    borderColor: '#403d35',
                    color: '#fffdf8',
                    px: 2,
                    py: 1.5,
                    '& .MuiSvgIcon-root': {
                      color: 'inherit',
                    },
                    '& .MuiChip-root': {
                      bgcolor: 'rgba(255, 253, 248, 0.08)',
                      borderColor: 'rgba(255, 253, 248, 0.28)',
                      color: 'inherit',
                    },
                  }}
                >
                  <Print />
                  <Typography variant="h6">{t('rendererPreview')}</Typography>
                  <Chip size="small" label={selectedLanguage?.label || bookForm.language} variant="outlined" />
                  {previewExport && (
                    <Chip
                      size="small"
                      label={`${formatTranslatedLabel(t, String(previewMetadata.renderer || previewExport.export_type))}`}
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {isPdfPreview && <Chip size="small" label="Book view" color="primary" variant="outlined" />}
                  {!isPdfPreview && previewPageCount > 0 && <Chip size="small" label={`${previewPageCount} ${t('pages')}`} variant="outlined" />}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    startIcon={<Refresh />}
                    onClick={onRefreshBookPreview}
                    disabled={saving || !currentBook}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255, 253, 248, 0.34)',
                      color: '#fffdf8',
                      '&:hover': {
                        bgcolor: 'rgba(255, 253, 248, 0.08)',
                        borderColor: 'rgba(255, 253, 248, 0.62)',
                      },
                      '&.Mui-disabled': {
                        borderColor: 'rgba(255, 253, 248, 0.16)',
                        color: 'rgba(255, 253, 248, 0.42)',
                      },
                    }}
                  >
                    {t('refreshPreview')}
                  </Button>
                </Stack>
                <Box sx={{ p: 2 }}>
                  {rendererPreviewUrl ? (
                  <Stack spacing={1.5}>
                    {previewPages.length > 0 && !isPdfPreview && (
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <IconButton
                            aria-label={t('previousPage')}
                            size="small"
                            onClick={() => setPreviewPageIndex((current) => Math.max(0, current - 1))}
                            disabled={selectedPreviewPageIndex === 0}
                          >
                            {previewPreviousIcon}
                          </IconButton>
                          <IconButton
                            aria-label={t('nextPage')}
                            size="small"
                            onClick={() => setPreviewPageIndex((current) => Math.min(previewPages.length - 1, current + 1))}
                            disabled={selectedPreviewPageIndex >= previewPages.length - 1}
                          >
                            {previewNextIcon}
                          </IconButton>
                        </Stack>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
                          <InputLabel id="book-preview-page-label">{t('page')}</InputLabel>
                          <Select
                            labelId="book-preview-page-label"
                            label={t('page')}
                            value={String(selectedPreviewPageIndex)}
                            onChange={(event) => setPreviewPageIndex(Number(event.target.value))}
                          >
                            {previewPages.map((page, index) => (
                              <MenuItem key={`${page.anchor || page.kind || 'page'}-${index}`} value={String(index)}>
                                {`${page.number || index + 1}. ${page.label || formatTranslatedLabel(t, String(page.kind || 'page'))}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                          {`${t('page')} ${selectedPreviewPageIndex + 1} / ${previewPages.length}`}
                        </Typography>
                      </Stack>
                    )}
                    {isPdfPreview && vivliostyleBookViewerUrl ? (
                      <Box
                        component="iframe"
                        key={previewCacheKey || vivliostyleBookViewerUrl}
                        title={t('renderedBookProof')}
                        src={vivliostyleBookViewerUrl}
                        sx={{
                          width: '100%',
                          height: { xs: 520, md: 680 },
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.default',
                        }}
                      />
                    ) : (
                      <Box
                        component="iframe"
                        title={t('renderedBookProof')}
                        src={pagedRendererPreviewUrl}
                        sx={{
                          width: '100%',
                          height: { xs: 520, md: 680 },
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.default',
                        }}
                      />
                    )}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        alignItems: 'center',
                        bgcolor: '#25231f',
                        border: '1px dashed',
                        borderColor: '#6f685b',
                        borderRadius: 1,
                        color: '#fffdf8',
                        display: 'flex',
                        minHeight: { xs: 360, md: 520 },
                        p: 3,
                        textAlign: 'center',
                      }}
                    >
                      <Stack spacing={1.5} sx={{ mx: 'auto', maxWidth: 420 }}>
                        <Print sx={{ color: 'rgba(255, 253, 248, 0.5)', fontSize: 48, mx: 'auto' }} />
                        <Typography variant="h6">{t('noExactPreviewYet')}</Typography>
                        <Typography sx={{ color: 'rgba(255, 253, 248, 0.72)' }}>{t('exactPreviewRequiresRenderer')}</Typography>
                        <Button
                          startIcon={<Refresh />}
                          onClick={onRefreshBookPreview}
                          disabled={saving || !currentBook}
                          variant="contained"
                        >
                          {t('refreshPreview')}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {isPublishingStage && (
              <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" mb={2}>
                {isDesignStage ? t('actions') : t('production')}
              </Typography>
              <Stack spacing={1.5}>
                {isDesignStage ? (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={onSaveBook}
                      disabled={saving || !currentBook}
                      fullWidth
                    >
                      {t('saveBook')}
                    </Button>
                    <TextField
                      label={t('versionNote')}
                      value={bookForm.versionNote}
                      onChange={(event) => setField('versionNote', event.target.value)}
                      fullWidth
                    />
                    <Button
                      startIcon={<AutoStories />}
                      onClick={onSaveBookVersion}
                      disabled={saving || !currentBook}
                      fullWidth
                    >
                      {t('saveVersion')}
                    </Button>
                    <Button
                      startIcon={<AssignmentTurnedIn />}
                      onClick={onApproveBook}
                      disabled={saving || !currentBook || currentBook.status === 'approved_for_export'}
                      fullWidth
                    >
                      {t('approveBook')}
                    </Button>
                  </>
                ) : (
                  <>
                    <FormControl fullWidth>
                      <InputLabel id="book-export-type-label">{t('exportType')}</InputLabel>
                      <Select
                        labelId="book-export-type-label"
                        label={t('exportType')}
                        value={bookForm.exportType}
                        onChange={(event) => setField('exportType', event.target.value)}
                      >
                        <MenuItem value="print_pdf">{formatTranslatedLabel(t, 'print_pdf')}</MenuItem>
                        <MenuItem value="proof_pdf">{formatTranslatedLabel(t, 'proof_pdf')}</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      startIcon={<Print />}
                      onClick={onCreateBookExport}
                      disabled={saving || !currentBook || currentBook.status !== 'approved_for_export'}
                      fullWidth
                    >
                      {t('requestExport')}
                    </Button>
                    <Button
                      startIcon={<FactCheck />}
                      onClick={() => latestExport && onMarkExportPrintReady(latestExport)}
                      disabled={saving || !latestExport}
                      fullWidth
                    >
                      {t('runPreflight')}
                    </Button>
                    <FormControl fullWidth>
                      <InputLabel id="book-print-provider-label">{t('printProvider')}</InputLabel>
                      <Select
                        labelId="book-print-provider-label"
                        label={t('printProvider')}
                        value={bookForm.publishingProvider}
                        onChange={(event) => setField('publishingProvider', event.target.value)}
                      >
                        {printProviderOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {formatTranslatedLabel(t, option.value)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label={t('publishingNotes')}
                      value={bookForm.publishingNotes}
                      onChange={(event) => setField('publishingNotes', event.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    <Button
                      variant="contained"
                      startIcon={<Send />}
                      onClick={() => printReadyExport && onCreatePublishingRequest(printReadyExport)}
                      disabled={saving || !printReadyExport}
                      fullWidth
                    >
                      {t('sendToPrinting')}
                    </Button>
                  </>
                )}
              </Stack>
              </Paper>
            )}

            {isPublishingStage && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="h6">{t('preflight')}</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={formatTranslatedLabel(t, asString(latestPreflightReport.provider, bookForm.publishingProvider))}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={formatTranslatedLabel(t, asString(latestPreflightReport.status, latestExport?.preflight_status || 'pending'))}
                        color={getPreflightStatusColor(asString(latestPreflightReport.status, latestExport?.preflight_status || 'pending'))}
                      />
                    </Stack>
                  </Stack>
                  {latestExport ? (
                    <>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap">
                        <Chip size="small" label={`${t('export')} ${latestExport.id}`} variant="outlined" />
                        <Chip size="small" label={`${failedPreflightCount} ${t('failedCount')}`} color={failedPreflightCount ? 'error' : 'success'} />
                        <Chip
                          size="small"
                          label={`${warningPreflightCount} ${t('warningsCount')}`}
                          color={warningPreflightCount ? 'warning' : 'success'}
                          variant={warningPreflightCount ? 'filled' : 'outlined'}
                        />
                        {asNumber(latestPreflightReport.page_count) !== undefined && (
                          <Chip size="small" label={`${asNumber(latestPreflightReport.page_count)} ${t('pages')}`} variant="outlined" />
                        )}
                      </Stack>
                      {latestPreflightChecks.length > 0 ? (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t('check')}</TableCell>
                                <TableCell>{t('status')}</TableCell>
                                <TableCell>{t('message')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {latestPreflightChecks.map((check, index) => (
                                <TableRow key={`${check.code || 'check'}-${index}`} hover>
                                  <TableCell>{formatTranslatedLabel(t, asString(check.code, 'check'))}</TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={formatTranslatedLabel(t, asString(check.status, 'pending'))}
                                      color={getPreflightStatusColor(asString(check.status, 'pending'))}
                                      variant={check.status === 'not_applicable' ? 'outlined' : 'filled'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {asString(check.message, t('noMessageRecorded'))}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography color="text.secondary">{t('noPreflightReport')}</Typography>
                      )}
                    </>
                  ) : (
                    <Typography color="text.secondary">{t('noExportReadyForPreflight')}</Typography>
                  )}
                </Stack>
              </Paper>
            )}

            {isPublishingStage && latestPublishingRequest && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="h6">{t('submissionTracker')}</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={formatTranslatedLabel(t, latestPublishingRequest.provider || 'publishing')}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={formatTranslatedLabel(t, latestPublishingRequest.status)}
                        color={getStatusColor(latestPublishingRequest.status)}
                      />
                      <Chip size="small" label={formatDate(latestPublishingRequest.submitted_at, locale, t('notRecorded'))} variant="outlined" />
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={0.75} flexWrap="wrap">
                    {['interior_pdf', 'cover_pdf', 'preflight_passed', 'metadata_ready', 'package_ready'].map((key) => (
                      <Chip
                        key={key}
                        size="small"
                        label={formatTranslatedLabel(t, key)}
                        color={latestPublishingChecklist[key] ? 'success' : 'warning'}
                        variant={latestPublishingChecklist[key] ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {asString(latestPublishingDownloads.interior_pdf) && (
                      <Button
                        href={asString(latestPublishingDownloads.interior_pdf)}
                        target="_blank"
                        rel="noreferrer"
                        startIcon={<OpenInNew />}
                        size="small"
                      >
	                        {t('interior')}
                      </Button>
                    )}
                    {asString(latestPublishingDownloads.cover_pdf) && (
                      <Button
                        href={asString(latestPublishingDownloads.cover_pdf)}
                        target="_blank"
                        rel="noreferrer"
                        startIcon={<OpenInNew />}
                        size="small"
                      >
	                        {t('cover')}
                      </Button>
                    )}
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
	                        <InputLabel id="publishing-status-label">{t('status')}</InputLabel>
                        <Select
                          labelId="publishing-status-label"
                          label={t('status')}
                          value={getPublishingDraftValue(latestPublishingRequest, 'status') || latestPublishingRequest.status}
                          onChange={(event) => setPublishingDraftField(latestPublishingRequest, 'status', event.target.value)}
                        >
                          {publishingStatusOptions.map((status) => (
                            <MenuItem key={status} value={status}>
                              {formatTranslatedLabel(t, status)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
	                        label={t('marketplace')}
                        value={getPublishingDraftValue(latestPublishingRequest, 'marketplace')}
                        onChange={(event) =>
                          setPublishingDraftField(latestPublishingRequest, 'marketplace', event.target.value)
                        }
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={t('isbn')}
                        value={getPublishingDraftValue(latestPublishingRequest, 'isbn')}
                        onChange={(event) => setPublishingDraftField(latestPublishingRequest, 'isbn', event.target.value)}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={t('asin')}
                        value={getPublishingDraftValue(latestPublishingRequest, 'asin')}
                        onChange={(event) => setPublishingDraftField(latestPublishingRequest, 'asin', event.target.value)}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={t('reference')}
                        value={getPublishingDraftValue(latestPublishingRequest, 'external_reference')}
                        onChange={(event) =>
                          setPublishingDraftField(latestPublishingRequest, 'external_reference', event.target.value)
                        }
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
	                        label={t('reviewerNotes')}
                        value={getPublishingDraftValue(latestPublishingRequest, 'reviewer_notes')}
                        onChange={(event) =>
                          setPublishingDraftField(latestPublishingRequest, 'reviewer_notes', event.target.value)
                        }
                        multiline
                        minRows={2}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <Button
                      startIcon={<Send />}
                      variant="outlined"
                      onClick={() =>
                        savePublishingDraft(latestPublishingRequest, {
                          status: 'submitted',
                          submitted_by_user_id: bookForm.createdByUserId ? Number(bookForm.createdByUserId) : undefined,
                        })
                      }
                      disabled={saving}
                    >
	                      {t('submitted')}
                    </Button>
                    <Button
                      startIcon={<AssignmentTurnedIn />}
                      variant="outlined"
                      onClick={() => savePublishingDraft(latestPublishingRequest, { status: 'published' })}
                      disabled={saving}
                    >
	                      {t('published')}
                    </Button>
                    <Button
                      startIcon={<Save />}
                      variant="contained"
                      onClick={() => savePublishingDraft(latestPublishingRequest)}
                      disabled={saving}
                    >
	                      {t('save')}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}

            {isPublishingStage && (
              <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" mb={2}>
                {t('history')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
	                      <TableCell>{t('type')}</TableCell>
	                      <TableCell>{t('status')}</TableCell>
	                      <TableCell>{t('created')}</TableCell>
	                      <TableCell align="right">{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isDesignStage && currentBookVersions.slice(-4).map((version) => (
                      <TableRow key={`version-${version.id}`} hover>
                        <TableCell>Version {version.version_number}</TableCell>
                        <TableCell>
                          <Chip size="small" label={formatTranslatedLabel(t, version.status)} color={getStatusColor(version.status)} />
                        </TableCell>
                        <TableCell>{formatDate(version.created_at, locale, t('notRecorded'))}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    ))}
                    {isPublishingStage && currentBookExports.map((bookExport) => (
                      <TableRow key={`export-${bookExport.id}`} hover>
                        <TableCell>{formatTranslatedLabel(t, bookExport.export_type)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            <Chip size="small" label={formatTranslatedLabel(t, bookExport.status)} color={getStatusColor(bookExport.status)} />
                            <Chip size="small" label={formatTranslatedLabel(t, bookExport.preflight_status)} variant="outlined" />
                          </Stack>
                        </TableCell>
                        <TableCell>{formatDate(bookExport.created_at, locale, t('notRecorded'))}</TableCell>
                        <TableCell align="right">
                          {project && currentBook && bookExport.storage_key && (
                            <Button
                              href={platformApi.getProjectBookExportDownloadUrl(project.id, currentBook.id, bookExport.id)}
                              target="_blank"
                              rel="noreferrer"
                              startIcon={<OpenInNew />}
                              size="small"
                            >
                              Open
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {isPublishingStage && currentPublishingRequests.map((request) => (
                      <TableRow key={`publishing-${request.id}`} hover>
                        <TableCell>{request.provider || t('publishing')}</TableCell>
                        <TableCell>
                          <Chip size="small" label={formatTranslatedLabel(t, request.status)} color={getStatusColor(request.status)} />
                        </TableCell>
                        <TableCell>{formatDate(request.created_at, locale, t('notRecorded'))}</TableCell>
                        <TableCell align="right">
                          {asString(getKdpPackageDownloads(request).interior_pdf) && (
                            <Button
                              href={asString(getKdpPackageDownloads(request).interior_pdf)}
                              target="_blank"
                              rel="noreferrer"
                              startIcon={<OpenInNew />}
                              size="small"
                            >
                              Interior
                            </Button>
                          )}
                          {asString(getKdpPackageDownloads(request).cover_pdf) && (
                            <Button
                              href={asString(getKdpPackageDownloads(request).cover_pdf)}
                              target="_blank"
                              rel="noreferrer"
                              startIcon={<OpenInNew />}
                              size="small"
                            >
                              Cover
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {((isDesignStage && currentBookVersions.length === 0) ||
                      (isPublishingStage && currentBookExports.length === 0 && currentPublishingRequests.length === 0)) && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                            No book history yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              </Paper>
            )}
          </Stack>
        </Grid>
        </Grid>
      )}

      <Dialog
        open={draftDialogOpen}
        onClose={() => {
          if (currentBook) {
            setDraftDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <MenuBook color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('bookDesignDrafts')}
              </Typography>
              {currentBook && (
                <Typography variant="body2" color="text.secondary">
                  {currentBook.title}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            {!currentBook && (
              <Alert severity="info">
                {t('selectOrCreateBookDesignDraft')}
              </Alert>
            )}
            {draftDialogError && (
              <Alert severity="error" onClose={() => setDraftDialogError(null)}>
                {draftDialogError}
              </Alert>
            )}
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: '#e0dbd2',
                borderRadius: 2,
                p: { xs: 2, md: 2.5 },
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Add />
                  <Typography variant="h6">{t('createBookDesignDraft')}</Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label={t('bookDesignDraftName')}
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth disabled={storyDocuments.length === 0}>
                      <InputLabel id="draft-source-artifact-label">{t('storyDocuments')}</InputLabel>
                      <Select
                        labelId="draft-source-artifact-label"
                        label={t('storyDocuments')}
                        value={draftSourceArtifactId}
                        onChange={(event) => setDraftSourceArtifactId(event.target.value)}
                      >
                        {storyDocuments.map((artifact) => (
                          <MenuItem key={artifact.id} value={artifact.id.toString()}>
                            {artifact.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id="draft-book-type-label">{t('bookType')}</InputLabel>
                      <Select
                        labelId="draft-book-type-label"
                        label={t('bookType')}
                        value={draftBookType}
                        onChange={(event) => setDraftBookType(event.target.value as BookType)}
                      >
                        <MenuItem value="text">{formatTranslatedLabel(t, 'text')}</MenuItem>
                        <MenuItem value="picture">{formatTranslatedLabel(t, 'picture')}</MenuItem>
                        <MenuItem value="mixed">{formatTranslatedLabel(t, 'mixed')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth disabled={templatesLoading}>
                      <InputLabel id="draft-template-label">{t('designTemplate')}</InputLabel>
                      <Select
                        labelId="draft-template-label"
                        label={t('designTemplate')}
                        value={draftTemplateId}
                        onChange={(event) => {
                          const nextTemplateId = event.target.value;
                          setDraftTemplateId(nextTemplateId);
                          const template = designTemplates.find((item) => item.id.toString() === nextTemplateId);
                          if (template && isBookType(template.book_type)) {
                            setDraftBookType(template.book_type);
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('noTemplate')}</em>
                        </MenuItem>
                        {designTemplates.map((template) => (
                          <MenuItem key={template.id} value={template.id.toString()}>
                            {template.name} ({formatTranslatedLabel(t, template.book_type)})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                {templatesError && <Alert severity="warning">{templatesError}</Alert>}
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" color="text.secondary">
                    {activeDraftCreator
                      ? `${t('creatingUser')}: ${activeDraftCreator.display_name || activeDraftCreator.email}`
                      : t('creatingUserRecordedFromSession')}
                  </Typography>
                  <Button variant="contained" startIcon={<Add />} onClick={handleCreateDraft} disabled={saving || !project}>
                    {t('createBookDesignDraft')}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Stack spacing={1}>
              <Typography variant="h6">{t('selectBookDesignDraft')}</Typography>
              {bookDesignDrafts.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                  {t('noBookDesignDraftsYet')}
                </Typography>
              ) : (
                <>
                  <Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
                    {bookDesignDrafts.map((draft) => {
                      const creator = users.find((user) => user.id === draft.created_by_user_id);
                      const isActiveDraft = selectedBookId === draft.id.toString();
                      const canDeleteDraft = draft.status !== 'approved_for_export';
                      return (
                        <Box
                          key={draft.id}
                          sx={{
                            bgcolor: '#fff',
                            border: '1px solid',
                            borderColor: isActiveDraft ? 'primary.main' : '#e0dbd2',
                            borderRadius: 2,
                            p: 1.5,
                          }}
                        >
                          <Stack spacing={1.25}>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                {draft.title}
                              </Typography>
                              {isActiveDraft && <Chip size="small" color="primary" label={t('active')} />}
                            </Stack>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Chip size="small" label={formatTranslatedLabel(t, draft.status)} color={getStatusColor(draft.status)} variant="outlined" />
                              <Chip size="small" label={formatTranslatedLabel(t, draft.book_type || 'text')} variant="outlined" />
                              <Chip size="small" label={formatDate(draft.created_at, locale, t('notRecorded'))} variant="outlined" />
                              <Chip size="small" label={creator?.display_name || creator?.email || t('notRecorded')} variant="outlined" />
                            </Stack>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                startIcon={<AutoStories />}
                                variant={isActiveDraft ? 'contained' : 'outlined'}
                                onClick={() => {
                                  onSelectBook(draft.id.toString());
                                  setDraftDialogOpen(false);
                                }}
                              >
                                {t('select')}
                              </Button>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDraft(draft.id)}
                                disabled={saving || !canDeleteDraft}
                                aria-label={t('deleteBookDesignDraft')}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                  <TableContainer
                    sx={{
                      bgcolor: '#fff',
                      border: '1px solid',
                      borderColor: '#e0dbd2',
                      borderRadius: 2,
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('title')}</TableCell>
                          <TableCell>{t('status')}</TableCell>
                          <TableCell>{t('bookType')}</TableCell>
                          <TableCell>{t('created')}</TableCell>
                          <TableCell>{t('user')}</TableCell>
                          <TableCell align="right">{t('actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bookDesignDrafts.map((draft) => {
                          const creator = users.find((user) => user.id === draft.created_by_user_id);
                          const isActiveDraft = selectedBookId === draft.id.toString();
                          const canDeleteDraft = draft.status !== 'approved_for_export';
                          return (
                            <TableRow key={draft.id} hover selected={isActiveDraft}>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography variant="body2" fontWeight={isActiveDraft ? 700 : 500}>
                                    {draft.title}
                                  </Typography>
                                  {isActiveDraft && <Chip size="small" color="primary" label={t('active')} />}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={formatTranslatedLabel(t, draft.status)} color={getStatusColor(draft.status)} variant="outlined" />
                              </TableCell>
                              <TableCell>{formatTranslatedLabel(t, draft.book_type || 'text')}</TableCell>
                              <TableCell>{formatDate(draft.created_at, locale, t('notRecorded'))}</TableCell>
                              <TableCell>{creator?.display_name || creator?.email || t('notRecorded')}</TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    startIcon={<AutoStories />}
                                    variant={isActiveDraft ? 'contained' : 'outlined'}
                                    onClick={() => {
                                      onSelectBook(draft.id.toString());
                                      setDraftDialogOpen(false);
                                    }}
                                  >
                                    {t('select')}
                                  </Button>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteDraft(draft.id)}
                                    disabled={saving || !canDeleteDraft}
                                    aria-label={t('deleteBookDesignDraft')}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
          <Button onClick={() => setDraftDialogOpen(false)} disabled={!currentBook}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={designDetailsOpen}
        onClose={() => setDesignDetailsOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Article color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('bookDetails')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentBook?.title || t('noActiveBookDesignDraft')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: '#e0dbd2',
                borderRadius: 2,
                p: { xs: 2, md: 2.5 },
              }}
            >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label={t('title')} value={bookForm.title} onChange={(event) => setField('title', event.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label={t('subtitle')} value={bookForm.subtitle} onChange={(event) => setField('subtitle', event.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('authorName')}
                  value={bookForm.authorName}
                  onChange={(event) => setField('authorName', event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="book-details-language-label">{t('language')}</InputLabel>
                  <Select
                    labelId="book-details-language-label"
                    label={t('language')}
                    value={bookForm.language}
                    onChange={(event) => {
                      const language = languageOptions.find((option) => option.value === event.target.value);
                      setBookForm((current) => ({
                        ...current,
                        language: event.target.value,
                        textDirection: language?.direction || current.textDirection,
                        textAlignment: language?.direction === 'rtl' ? 'right' : current.textAlignment,
                      }));
                    }}
                  >
                    {languageOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="book-details-direction-label">{t('direction')}</InputLabel>
                  <Select
                    labelId="book-details-direction-label"
                    label={t('direction')}
                    value={bookForm.textDirection}
                    onChange={(event) => setField('textDirection', event.target.value)}
                  >
                    <MenuItem value="ltr">{t('leftToRight')}</MenuItem>
                    <MenuItem value="rtl">{t('rightToLeft')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl fullWidth>
                  <InputLabel id="book-details-source-label">{t('storyDocument')}</InputLabel>
                  <Select
                    labelId="book-details-source-label"
                    label={t('storyDocument')}
                    value={bookForm.sourceArtifactId}
                    onChange={(event) => setField('sourceArtifactId', event.target.value)}
                  >
                    {storyDocuments.map((artifact) => (
                      <MenuItem key={artifact.id} value={artifact.id.toString()}>
                        {artifact.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('versionNote')}
                  value={bookForm.versionNote}
                  onChange={(event) => setField('versionNote', event.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            </Box>
            <TableContainer
              sx={{
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: '#e0dbd2',
                borderRadius: 2,
                overflowX: 'auto',
              }}
            >
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('type')}</TableCell>
                    <TableCell>{t('title')}</TableCell>
                    <TableCell>{t('source')}</TableCell>
                    <TableCell>{t('status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {designArtifacts.map((artifact) => (
                    <TableRow key={artifact.id} hover>
                      <TableCell>{formatTranslatedLabel(t, artifact.type)}</TableCell>
                      <TableCell>{artifact.title}</TableCell>
                      <TableCell>{artifact.source}</TableCell>
                      <TableCell>
                        <Chip size="small" label={formatTranslatedLabel(t, artifact.status)} color={getStatusColor(artifact.status)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
          <Button onClick={() => setDesignDetailsOpen(false)}>{t('cancel')}</Button>
          <Button
            startIcon={<Save />}
            variant="contained"
            onClick={() => {
              onSaveBook();
              setDesignDetailsOpen(false);
            }}
            disabled={saving || !currentBook}
          >
            {t('saveBook')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={photoDialogOpen} onClose={() => setPhotoDialogOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <AddPhotoAlternate color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('addPhoto')}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={photoArtifacts.length === 0}>
                <InputLabel id="photo-dialog-artifact-label">{t('photo')}</InputLabel>
                <Select
                  labelId="photo-dialog-artifact-label"
                  label={t('photo')}
                  value={bookForm.photoArtifactId}
                  onChange={(event) => setField('photoArtifactId', event.target.value)}
                >
                  {photoArtifacts.map((artifact) => (
                    <MenuItem key={artifact.id} value={artifact.id.toString()}>
                      {artifact.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="photo-dialog-placement-label">{t('placement')}</InputLabel>
                <Select
                  labelId="photo-dialog-placement-label"
                  label={t('placement')}
                  value={bookForm.photoPlacementMode}
                  onChange={(event) => setField('photoPlacementMode', event.target.value)}
                >
                  <MenuItem value="after_paragraph">{formatTranslatedLabel(t, 'after_paragraph')}</MenuItem>
                  <MenuItem value="top_of_next_page">{formatTranslatedLabel(t, 'top_of_next_page')}</MenuItem>
                  <MenuItem value="full_page">{formatTranslatedLabel(t, 'full_page')}</MenuItem>
                  <MenuItem value="absolute">{formatTranslatedLabel(t, 'absolute')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {bookForm.photoPlacementMode === 'after_paragraph' ? (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={paragraphAnchorOptions.length === 0}>
                  <InputLabel id="photo-dialog-anchor-label">{t('anchor')}</InputLabel>
                  <Select
                    labelId="photo-dialog-anchor-label"
                    label={t('anchor')}
                    value={bookForm.photoAnchorParagraphId}
                    onChange={(event) => setField('photoAnchorParagraphId', event.target.value)}
                  >
                    {paragraphAnchorOptions.map((paragraph) => (
                      <MenuItem key={paragraph.id} value={paragraph.id}>
                        {paragraph.label}: {paragraph.preview}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="photo-dialog-page-side-label">{t('pageSide')}</InputLabel>
                  <Select
                    labelId="photo-dialog-page-side-label"
                    label={t('pageSide')}
                    value={bookForm.photoPageSide}
                    onChange={(event) => setField('photoPageSide', event.target.value)}
                  >
                    <MenuItem value="next">{formatTranslatedLabel(t, 'next')}</MenuItem>
                    <MenuItem value="left">{formatTranslatedLabel(t, 'left')}</MenuItem>
                    <MenuItem value="right">{formatTranslatedLabel(t, 'right')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {[
              ['photoXPercent', 'xPercent'],
              ['photoYPercent', 'yPercent'],
              ['photoWidthPercent', 'widthPercent'],
              ['photoHeightPercent', 'heightPercent'],
              ['photoRotationDegrees', 'rotate'],
            ].map(([field, label]) => (
              <Grid key={field} item xs={6} md={2.4}>
                <TextField
                  label={t(label)}
                  type="number"
                  value={bookForm[field as keyof BookDesignFormState] as string}
                  onChange={(event) => setField(field as keyof BookDesignFormState, event.target.value)}
                  fullWidth
                />
              </Grid>
            ))}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="photo-dialog-align-label">{t('align')}</InputLabel>
                <Select
                  labelId="photo-dialog-align-label"
                  label={t('align')}
                  value={bookForm.photoAlignment}
                  onChange={(event) => setField('photoAlignment', event.target.value)}
                >
                  <MenuItem value="left">{formatTranslatedLabel(t, 'left')}</MenuItem>
                  <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                  <MenuItem value="right">{formatTranslatedLabel(t, 'right')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField label={t('caption')} value={bookForm.photoCaption} onChange={(event) => setField('photoCaption', event.target.value)} fullWidth />
            </Grid>
            {bookForm.imagePlacements.length > 0 && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('photo')}</TableCell>
                        <TableCell>{t('reference')}</TableCell>
                        <TableCell>{t('layout')}</TableCell>
                        <TableCell align="right">{t('actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bookForm.imagePlacements.map((placement) => {
                        const artifact = photoArtifacts.find((item) => item.id.toString() === placement.artifactId);
                        return (
                          <TableRow key={placement.id} hover>
                            <TableCell>{artifact?.title || `Photo #${placement.artifactId}`}</TableCell>
                            <TableCell>
                              {placement.placementMode === 'after_paragraph'
                                ? placement.anchorParagraphId
                                : `${t('pageSide')}: ${formatTranslatedLabel(t, placement.pageSide)}`}
                            </TableCell>
                            <TableCell>
                              {formatTranslatedLabel(t, placement.placementMode)}, {placement.widthPercent}% x {placement.heightPercent}%
                            </TableCell>
                            <TableCell align="right">
                              <IconButton aria-label={t('removePhoto')} onClick={() => handleRemovePhotoPlacement(placement.id)} size="small">
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
          <Button onClick={() => setPhotoDialogOpen(false)}>{t('cancel')}</Button>
          <Button
            startIcon={<AddPhotoAlternate />}
            variant="contained"
            onClick={() => {
              handleAddPhotoPlacement();
              setPhotoDialogOpen(false);
            }}
            disabled={!bookForm.photoArtifactId}
          >
            {t('addPhoto')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={coverDialogOpen} onClose={() => setCoverDialogOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <AutoStories color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('coverDesign')}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="cover-dialog-side-label">{t('side')}</InputLabel>
                <Select
                  labelId="cover-dialog-side-label"
                  label={t('side')}
                  value={bookForm.coverSide}
                  onChange={(event) => setBookForm((current) => ({ ...current, coverSide: event.target.value as 'front' | 'back' }))}
                >
                  <MenuItem value="front">{formatTranslatedLabel(t, 'front')}</MenuItem>
                  <MenuItem value="back">{formatTranslatedLabel(t, 'back')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="cover-dialog-type-label">{t('type')}</InputLabel>
                <Select
                  labelId="cover-dialog-type-label"
                  label={t('type')}
                  value={bookForm.coverItemType}
                  onChange={(event) => setBookForm((current) => ({ ...current, coverItemType: event.target.value as 'photo' | 'text' }))}
                >
                  <MenuItem value="photo">{t('photo')}</MenuItem>
                  <MenuItem value="text">{formatTranslatedLabel(t, 'text')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {bookForm.coverItemType === 'photo' ? (
                <FormControl fullWidth disabled={photoArtifacts.length === 0}>
                  <InputLabel id="cover-dialog-photo-label">{t('photo')}</InputLabel>
                  <Select
                    labelId="cover-dialog-photo-label"
                    label={t('photo')}
                    value={bookForm.coverPhotoArtifactId}
                    onChange={(event) => setField('coverPhotoArtifactId', event.target.value)}
                  >
                    {photoArtifacts.map((artifact) => (
                      <MenuItem key={artifact.id} value={artifact.id.toString()}>
                        {artifact.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField label={t('coverText')} value={bookForm.coverText} onChange={(event) => setField('coverText', event.target.value)} fullWidth />
              )}
            </Grid>
            {[
              ['coverXPercent', 'xPercent'],
              ['coverYPercent', 'yPercent'],
              ['coverWidthPercent', 'widthPercent'],
              ['coverHeightPercent', 'heightPercent'],
              ['coverRotationDegrees', 'rotate'],
            ].map(([field, label]) => (
              <Grid key={field} item xs={6} md={2.4}>
                <TextField
                  label={t(label)}
                  type="number"
                  value={bookForm[field as keyof BookDesignFormState] as string}
                  onChange={(event) => setField(field as keyof BookDesignFormState, event.target.value)}
                  fullWidth
                />
              </Grid>
            ))}
            {bookForm.coverItemType === 'text' && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="cover-dialog-font-label">{t('font')}</InputLabel>
                    <Select
                      labelId="cover-dialog-font-label"
                      label={t('font')}
                      value={bookForm.coverFontFamily}
                      onChange={(event) => setField('coverFontFamily', event.target.value)}
                    >
                      <MenuItem value="">{t('coverDefaultFont')}</MenuItem>
                      {bookFontFamilyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('fontSize')}
                    type="number"
                    value={bookForm.coverFontSizePt}
                    onChange={(event) => setField('coverFontSizePt', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label={t('color')}
                    type="color"
                    value={bookForm.coverColor || '#000000'}
                    onChange={(event) => setField('coverColor', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="cover-dialog-align-label">{t('textAlign')}</InputLabel>
                    <Select
                      labelId="cover-dialog-align-label"
                      label={t('textAlign')}
                      value={bookForm.coverTextAlign}
                      onChange={(event) => setField('coverTextAlign', event.target.value)}
                    >
                      <MenuItem value="left">{t('left')}</MenuItem>
                      <MenuItem value="center">{t('center')}</MenuItem>
                      <MenuItem value="right">{t('right')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Stack direction="row" spacing={0.5} sx={{ height: '100%' }} alignItems="center">
                    <FormControlLabel
                      control={<Checkbox checked={bookForm.coverBold} onChange={(event) => setField('coverBold', event.target.checked)} />}
                      label={t('bold')}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={bookForm.coverItalic} onChange={(event) => setField('coverItalic', event.target.checked)} />}
                      label={t('italic')}
                    />
                  </Stack>
                </Grid>
              </>
            )}
            <Grid item xs={6} md={3}>
              <TextField
                label={t('coverBackgroundFront')}
                type="color"
                value={bookForm.coverFrontBackgroundColor || '#ffffff'}
                onChange={(event) => setField('coverFrontBackgroundColor', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label={t('coverBackgroundBack')}
                type="color"
                value={bookForm.coverBackBackgroundColor || '#ffffff'}
                onChange={(event) => setField('coverBackBackgroundColor', event.target.value)}
                fullWidth
              />
            </Grid>
            {bookForm.coverItems.length > 0 && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('side')}</TableCell>
                        <TableCell>{t('item')}</TableCell>
                        <TableCell>{t('position')}</TableCell>
                        <TableCell align="right">{t('actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bookForm.coverItems.map((item) => {
                        const artifact = photoArtifacts.find((photo) => photo.id.toString() === item.artifactId);
                        return (
                          <TableRow key={item.id} hover>
                            <TableCell>{formatTranslatedLabel(t, item.side)}</TableCell>
                            <TableCell>{item.itemType === 'photo' ? artifact?.title || `Photo #${item.artifactId}` : item.text}</TableCell>
                            <TableCell>
                              {item.xPercent}/{item.yPercent}, {item.widthPercent}% x {item.heightPercent}%
                            </TableCell>
                            <TableCell align="right">
                              <IconButton aria-label={t('removeCoverItem')} onClick={() => handleRemoveCoverItem(item.id)} size="small">
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
          <Button onClick={() => setCoverDialogOpen(false)}>{t('cancel')}</Button>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => {
              handleAddCoverItem();
              setCoverDialogOpen(false);
            }}
            disabled={(bookForm.coverItemType === 'photo' && !bookForm.coverPhotoArtifactId) || (bookForm.coverItemType === 'text' && !bookForm.coverText.trim())}
          >
            {t('addCover')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={contentDialogOpen} onClose={() => setContentDialogOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <MenuBook color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('bookContent')}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <FormControl size="small" sx={{ maxWidth: 240 }}>
              <InputLabel id="book-type-label">{t('bookType')}</InputLabel>
              <Select
                labelId="book-type-label"
                label={t('bookType')}
                value={bookForm.bookType}
                onChange={(event) => setBookType(event.target.value as BookType)}
              >
                <MenuItem value="text">{formatTranslatedLabel(t, 'text')}</MenuItem>
                <MenuItem value="picture">{formatTranslatedLabel(t, 'picture')}</MenuItem>
                <MenuItem value="mixed">{formatTranslatedLabel(t, 'mixed')}</MenuItem>
              </Select>
            </FormControl>
            <Accordion disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">
                  {t('frontMatter')} ({bookForm.manuscript.front_matter?.length ?? 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FlowEditor
                  units={bookForm.manuscript.front_matter ?? []}
                  onUnitsChange={(units) => setMatter('front_matter', units)}
                  photoArtifacts={photoArtifacts}
                  projectId={project?.id}
                  textDirection={bookForm.textDirection}
                  pageWidthCm={Number(bookForm.pageWidthCm) || 15.24}
                  pageHeightCm={Number(bookForm.pageHeightCm) || 22.86}
                  disabled={saving}
                  t={t}
                />
              </AccordionDetails>
            </Accordion>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('bodyContent')}
              </Typography>
              <FlowEditor
                units={bookForm.manuscript.content ?? []}
                onUnitsChange={(units) => setMatter('content', units)}
                photoArtifacts={photoArtifacts}
                projectId={project?.id}
                textDirection={bookForm.textDirection}
                pageWidthCm={Number(bookForm.pageWidthCm) || 15.24}
                pageHeightCm={Number(bookForm.pageHeightCm) || 22.86}
                disabled={saving}
                t={t}
              />
            </Box>
            <Accordion disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">
                  {t('backMatter')} ({bookForm.manuscript.back_matter?.length ?? 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FlowEditor
                  units={bookForm.manuscript.back_matter ?? []}
                  onUnitsChange={(units) => setMatter('back_matter', units)}
                  photoArtifacts={photoArtifacts}
                  projectId={project?.id}
                  textDirection={bookForm.textDirection}
                  pageWidthCm={Number(bookForm.pageWidthCm) || 15.24}
                  pageHeightCm={Number(bookForm.pageHeightCm) || 22.86}
                  disabled={saving}
                  t={t}
                />
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
          <Button onClick={() => setContentDialogOpen(false)}>{t('close')}</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={() => {
              onApplyDesignParameters();
              setContentDialogOpen(false);
            }}
            disabled={saving || !currentBook}
          >
            {t('applyAndRefreshPreview')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={backgroundDialogOpen} onClose={() => setBackgroundDialogOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Wallpaper color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('pageBackground')}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('pageBackgroundHelp')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={bookForm.defaultBackgroundEnabled}
                  onChange={(event) => setField('defaultBackgroundEnabled', event.target.checked)}
                />
              }
              label={t('enableDefaultBackground')}
            />
            {bookForm.defaultBackgroundEnabled && (
              <>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{t('backgroundScope')}</InputLabel>
                    <Select
                      label={t('backgroundScope')}
                      value={bookForm.defaultBackgroundMode}
                      onChange={(event) => setField('defaultBackgroundMode', event.target.value)}
                    >
                      <MenuItem value="all">{t('allPages')}</MenuItem>
                      <MenuItem value="sides">{t('differentSides')}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{t('placement')}</InputLabel>
                    <Select
                      label={t('placement')}
                      value={bookForm.defaultBackgroundPlacement}
                      onChange={(event) => setField('defaultBackgroundPlacement', event.target.value)}
                    >
                      <MenuItem value="cover">{t('placementCover')}</MenuItem>
                      <MenuItem value="contain">{t('placementContain')}</MenuItem>
                      <MenuItem value="stretch">{t('placementStretch')}</MenuItem>
                      <MenuItem value="tile">{t('placementTile')}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                {bookForm.defaultBackgroundMode === 'all' ? (
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    {renderBackgroundPhotoPicker('defaultBackgroundAllArtifactId', t('allPages'))}
                    {renderBackgroundPreview(bookForm.defaultBackgroundAllArtifactId)}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {renderBackgroundPhotoPicker('defaultBackgroundLeftArtifactId', t('sideLeft'))}
                      {renderBackgroundPreview(bookForm.defaultBackgroundLeftArtifactId)}
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {renderBackgroundPhotoPicker('defaultBackgroundRightArtifactId', t('sideRight'))}
                      {renderBackgroundPreview(bookForm.defaultBackgroundRightArtifactId)}
                    </Stack>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
          <Button onClick={() => setBackgroundDialogOpen(false)}>{t('close')}</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={() => {
              onApplyDesignParameters();
              setBackgroundDialogOpen(false);
            }}
            disabled={saving || !currentBook}
          >
            {t('applyAndRefreshPreview')}
          </Button>
        </DialogActions>
      </Dialog>

      {renderParameterDialog(fontsDialogOpen, () => setFontsDialogOpen(false), t('fonts'), renderFontsTable())}
      {renderParameterDialog(
        printDialogOpen,
        () => setPrintDialogOpen(false),
        t('printParameters'),
        renderSettingsTable(printParameterRows)
      )}
      {renderParameterDialog(
        indentDialogOpen,
        () => setIndentDialogOpen(false),
        t('indentations'),
        renderSettingsTable(indentationRows)
      )}
      {renderParameterDialog(tocDialogOpen, () => setTocDialogOpen(false), t('contentIndex'), renderSettingsTable(tocRows))}
      {renderParameterDialog(rulesDialogOpen, () => setRulesDialogOpen(false), t('rules'), renderSettingsTable(rulesRows))}

      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Palette color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('applyDesignTemplate')}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfaf7', p: { xs: 2, md: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('applyingTemplateReplacesSettings')}
          </Typography>
          {templateAppliedNotice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTemplateAppliedNotice(false)}>
              {t('templateApplied')}
            </Alert>
          )}
          {templatesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {templatesError}
            </Alert>
          )}
          {templatesLoading ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : designTemplates.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              {t('noDesignTemplatesAvailable')}
            </Typography>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="design-template-select-label">{t('selectTemplate')}</InputLabel>
              <Select
                labelId="design-template-select-label"
                label={t('selectTemplate')}
                value={selectedTemplateId}
                onChange={(event) => {
                  setSelectedTemplateId(event.target.value);
                  setTemplateAppliedNotice(false);
                }}
              >
                {designTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id.toString()}>
                    {template.name} ({formatTranslatedLabel(t, template.book_type)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
          <Button onClick={() => setTemplateDialogOpen(false)}>{t('close')}</Button>
          <Button variant="contained" onClick={handleApplySelectedTemplate} disabled={!selectedTemplateId}>
            {t('applyTemplate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
